/* Ring builder — the gemstone step (step 2).
   Split out of ring-builder-pages.js because it is the most involved surface in
   the funnel and follows its own spec (scratchpad/STONE_BROWSER_SPEC.md, from a
   forensic teardown of Blue Nile and Brilliant Earth).

   THE THREE THINGS THAT MAKE IT READ AS PREMIUM, all learned from that teardown:
     1. The 4Cs are ORDERED-SCALE DUAL SLIDERS, not chips. Both benchmarks do this
        and neither uses a single checkbox among the 4Cs. Out-of-range grades are
        GRAYED, never hidden — seeing the whole ladder is what silently teaches a
        first-time buyer what VS1 means.
     2. TWO RAILS, and the color rail is SIMPLER, not richer. Brilliant Earth's
        diamond page has 19 filters; its gemstone page has 9 and no carat slider at
        all. Blue Nile's gemstone finder is four filters. Leading a color rail with
        the 4Cs would be a category error — a sapphire is not graded like a diamond.
     3. There is no "fits this setting" checkbox. Fit is a constraint on the control
        surface: incompatible shapes are DISABLED. Blue Nile has no such checkbox
        for exactly this reason.

   THE SLIDERS ARE THE GEMSTONE SEARCH'S OWN CONTROL (2026-09-02). `RB.rangeControl`
   / `RB.rangeRow` / `RB.contRow` on the engine (assets/ring-builder.js) ARE the
   reference's slider, moved there so this rail and
   /collections/all-gemstones?view=stone-search draw it row for row from one
   implementation: two native range inputs on one track, styled by
   stone-browse.css, no library. The library-drawn lookalike this file used to
   build is gone, with its CSS block and its two theme tags. */
(function () {
  'use strict';
  var RB = window.RingBuilder;
  if (!RB) return;
  var el = RB.el, money = RB.money;

  var SHAPES = ['ROUND', 'OVAL', 'CUSHION', 'EMERALD', 'PEAR', 'RADIANT', 'SQUARE',
    'ASSCHER', 'MARQUISE', 'HEART', 'ELONGATED CUSHION', 'ANTIQUE CUSHION',
    'TRILLION', 'ELONGATED HEXAGON', 'OCTAGON', 'ELONGATED OVAL', 'STRAIGHT BAGUETTE'];

  // Ordered worst -> best, so a dual slider reads left-to-right as rising quality.
  //
  // 🚨 A GRADE MISSING FROM THESE LISTS IS NOT FILTERED OUT — IT IS FILTERED PAST.
  // `passes()` below reads `if (r._col >= 0 && …)`, and an unlisted grade gives
  // indexOf === -1, which SKIPS the guard: the stone then passes at EVERY slider
  // position and shows up inside ranges that exclude it. That is not a crash and
  // not an empty grid — it is a K-colour stone sitting in a "D–F only" result set,
  // looking exactly like a correct result. So these lists must cover every grade
  // the shards can hold. `verify_diamond_assets.py` parses this file and fails on
  // any grade the data carries and the ladder does not; run it after editing here.
  //
  // 🚨 K-D IS THE SCALE, AND AN OUTLIER DOES NOT GET TO REDRAW IT. `U-V` was briefly
  // added as a ninth rung because ONE stone of 7,991 carries it. That is the wrong
  // trade: it hands every shopper a rung they will never want, implies we stock the
  // L-through-T grades between it and K (we stock none), and stretches the eight
  // grades that matter into 8/9ths of the track. Grades off this ladder are handled
  // as OFF-SCALE instead — excluded from the slider, surfaced in their own chip row
  // — which is the same treatment fancy colour already gets and for the same reason:
  // they are not "worse K", they are not on this axis at all. See isOffScale below.
  var COLOR = ['K', 'J', 'I', 'H', 'G', 'F', 'E', 'D'];
  // SI3 sits between I1 and SI2; FL (Flawless) is the one grade above IF.
  // 🚨 I2 AND I3 ARE RUNGS AS OF 2026-08-06 (owner ruling). They are REAL grades and
  // we hold 64 of them — I2 50, I3 14 — so unlike the colour ladder's U-V case there
  // is no "one stone in 7,991 stretching the track" argument against them, and they
  // sit contiguously below I1 rather than implying grades we do not stock.
  // ⚠️ ADDING RUNGS SHIFTS EVERY SLIDER INDEX. `clmin`/`clmax` are POSITIONS, so a
  // filter URL saved before this now means two grades lower. Nothing in the theme
  // hardcodes one — the presets below go through `CLARITY.indexOf` and the default
  // max is `CLARITY.length` — but a bookmarked or shared `?clmin=` link does, and
  // there is no way to migrate one. Accepted knowingly; the alternative is 64 stones
  // that no clarity search can reach.
  var CLARITY = ['I3', 'I2', 'I1', 'SI3', 'SI2', 'SI1',
                 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];
  // 🚨 EXCELLENT IS ITS OWN RUNG, AND IT IS THE BIGGEST ONE. This ladder used to
  // read Fair/Good/Very Good/Ideal/Super Ideal and CUT_MAP folded `excellent` onto
  // the same index as `ideal`, on the theory that "labs use different words for the
  // same top grade". Measured over the 7,991 diamonds, that theory is wrong:
  //
  //     Excellent 3,200 (40.0%)   GIA 1,209 · IGI 1,076 · GCAL 911
  //     Very Good 2,567 (32.1%)
  //     Ideal     1,688 (21.1%)   IGI 955 · GCAL 702 · EGL 18 · AGS 13
  //     8X          508 ( 6.4%)   GCAL only
  //     Good         28 ( 0.4%)
  //
  // IGI and GCAL each issue BOTH words on different stones, so within a single lab
  // they are not synonyms — Ideal sits above Excellent. Folding them meant a shopper
  // asking for "Ideal and better" was handed Excellent stones too, and the single
  // most common grade in the catalogue had no rung bearing its name. GIA simply tops
  // out at Excellent (it does not issue "Ideal"), which is normal and now shows
  // truthfully rather than being relabelled.
  //
  // `Fair` stays despite ZERO diamonds carrying it: the BUILDER's shards hold 2, and
  // this ladder is shared. `Super Ideal` is the trade-readable label for GCAL's "8X"
  // (eight Excellent sub-grades) — the card still prints the lab's own word, because
  // showing "Super Ideal" beside a GCAL report number would misquote the report.
  var CUT = ['Fair', 'Good', 'Very Good', 'Excellent', 'Ideal', 'Super Ideal'];

  // FANCY COLOUR IS A DIFFERENT AXIS, NOT A WORSE GRADE. A Fancy Vivid Blue is not
  // "below K" — it is off the D-Z scale entirely, and putting it at either end of
  // that ladder would be a category error. So fancy stones are excluded from the
  // colour slider and get their own chips (see the Fancy color facet below).
  // `'Fa'` is Stuller's truncated code for the same thing; the metafield was
  // repaired to the real words by write_fancy_colour.py, but the BUILDER's shards
  // still carry the old code until they are next rebuilt, so both are matched.
  // GIA grades the tinted end of the D-Z scale in PAIRS — L, M, N, then O-P, Q-R,
  // S-T, U-V, W-X, Y-Z — so a single stone reads "U-V", not "U". Declared up here
  // because `isFancy` below has to be able to tell a real off-ladder DESIGNATION
  // from a hue; see `offKey` for why they are bucketed under one chip.
  // ⚠️ ASCII HYPHEN, NOT AN EN-DASH. This value goes into the URL as `offcolor=`,
  // and an en-dash encodes to %E2%80%93 — a filter link nobody can read, type or
  // paste back reliably. Typography belongs in the label, not in the key.
  var BELOW_K = /^(?:[L-Z]|O-P|Q-R|S-T|U-V|W-X|Y-Z)$/i;

  // 🚨 A BAND IS A GRADE, NOT AN "OTHER COLOUR". Stuller states a calibrated stone's
  // colour as a RANGE, because the stone is one of a size-banded run rather than an
  // individually graded serial: `G-H`, `F-G`, `I-J`, run-together `Ghi`, and `F+`
  // meaning "F or better". None of those are single letters, so `COLOR.indexOf` gave
  // -1 and every one of them fell through to isOffScale and was listed under
  // "Other color — warmer than K". Measured live on 2026-08-06 that row held 524
  // stones of which exactly ONE was genuinely warmer than K; 410 were these bands,
  // and 261 of those were near-colourless G/H/I — the opposite of what the label
  // said. Worse than mislabelled: `passes()` DROPS an off-scale stone the moment the
  // colour slider leaves wide-open, so 123 natural G-H diamonds returned nothing for
  // a "G to D" search.
  //
  // ⚠️ THE BAND RESOLVES TO ITS LOWEST LETTER — owner rule 2026-08-06, and it is the
  // trade convention: quote the worst grade the stone can be, never the best. G-H is
  // filed under H, I-J under J, Ghi under I, F+ under F. The CARD still prints the
  // band exactly as Stuller states it, so nothing is hidden and nothing is inflated.
  //
  // ⚠️ `[D-N]`, NEVER `[D-Z]`. This is the same trap `loose_stone_copy._grade()`
  // documents: `[D-Z]{2,3}` also matches "Red", and a hue is not a grade. Within
  // D-N, "Gray" is excluded by length (4), "Fa" because `a` is not in the class,
  // and "Pink"/"Black"/"Cognac" by both.
  // ⚠️ THE RUN-TOGETHER FORM IS EXACTLY THREE LETTERS (`[D-N]{2}` after the first).
  // `{1,2}` also matched a coloured stone's two-letter HUE code — `De` (Demantoid),
  // `In` (Indicolite), `Mi`, `He`: 37 stones, and every 2-char [D-N] value in the
  // catalogue is one of them, with ZERO diamonds using a 2-char band. So `De`
  // resolved to colour grade E. It has no live effect (the colour guard runs only
  // inside the diamond branch, and coloured stones never reach the diamond page) but
  // it is the same latent trap `loose_stone_copy._GRADE_TOKEN` carried, and these two
  // patterns must agree or the Python and the browser disagree about what a band is.
  var BAND = /^[D-N](?:[-\/][D-N]|[D-N]{2})?\+?$/i;
  // Every colour letter the value mentions, worst (latest) first.
  var bandLetters = function (c) {
    var m = String(c || '').toUpperCase().match(/[D-N]/g);
    return m ? m.sort() : [];
  };
  // Index on the K-D ladder for a letter OR a band; -1 when it is neither.
  var colIndex = function (c) {
    c = String(c || '').trim();
    var i = COLOR.indexOf(c);
    if (i >= 0) return i;
    if (!BAND.test(c)) return -1;
    var ls = bandLetters(c);
    // the LOWEST grade in the band is the letter latest in the alphabet
    for (var k = ls.length - 1; k >= 0; k--) {
      var j = COLOR.indexOf(ls[k]);
      if (j >= 0) return j;
    }
    return -1;                       // e.g. "L-M" — a real designation, off-ladder
  };

  // A HUE IS ANYTHING THAT IS NOT A GRADE. Stuller writes natural fancies as
  // "Fancy Vivid Green" and colour-ENHANCED stones as a bare hue — "Apple Green",
  // "Aqua Blue", "Cognac". Both are fancy COLOUR (the grading vocabulary applies to
  // treated stones as well as natural-colour ones; what differs is colour ORIGIN,
  // which the title discloses and the Treatment facet filters). Owner decision
  // 2026-08-06: one Fancy color row, not two. Anything that is neither a grade nor
  // a real D-Z designation is therefore a hue.
  var isFancy = function (c) {
    c = String(c || '').trim();
    if (!c) return false;
    if (/^fancy/i.test(c) || c === 'Fa') return true;
    return colIndex(c) < 0 && !BELOW_K.test(c) && !BAND.test(c);
  };
  // 🚨 CLARITY HAS THE SAME SHAPE OF BUG COLOUR HAD, AND IT WAS LOUDER: 459 stones
  // of 8,128 carried a clarity the ladder did not list, and `passes()` reads
  // `r._cla >= 0` — so an unmatched grade SKIPPED the guard and every one of them
  // passed the clarity slider AT EVERY POSITION. Found by verify_diamond_assets.py
  // 2026-08-06, arriving with the 523 calibrated diamonds imported the day before.
  //
  // Three of the four causes are handled here; the other two need owner rulings and
  // are deliberately still off-ladder (see the note at the bottom of this block).
  //
  // 1. CASING. Stuller title-cases a grade the trade upper-cases — `Si1`, `Vvs2`.
  //    The product TITLE is already normalised by loose_stone_copy._grade(); the
  //    SHARD was not, which is why a card reads "F+ SI1" above "0.33 ct · F+ · Si1".
  // 2. FAMILY. A bare `Vs` or `Si` names a grade family, not a rung — Stuller grades
  //    these size-banded runs to the family only.
  //    ⚠️ `Si` IS NOT A CASING VARIANT OF `Si1`, and reading it as one was the first
  //    wrong turn here. Within series DIAMOND-GEN Stuller writes `Si`, `Si1` AND
  //    `Si2-Si3` as distinct values, so bare `Si` is its own band. Because they
  //    spell SI3 out whenever they mean it, `Si` is read as **SI1-SI2** and files
  //    under SI2 — not SI3, which would push 135 stones a rung below what the
  //    supplier is actually saying.
  // 3. BANDS. `Si1-Si2`, `Si2-Si3`, `Vs/Si1`, `Vs/Si` — hyphen or slash, same idea
  //    as the colour bands and the SAME owner rule (2026-08-06): file under the
  //    LOWEST grade in the range, never the best. The card prints the band verbatim.
  // The ladder's complete set of multi-rung families, each resolving to its LOWEST
  // rung. `I` was held back while I2/I3 were still an open question; with those now
  // rungs (owner ruling 2026-08-06) a bare `I` resolves like any other family.
  var CLARITY_FAMILY = { VS: 'VS2', SI: 'SI2', VVS: 'VVS2', I: 'I3' };
  // Every clarity token a value mentions, upper-cased. Split on hyphen or slash so
  // `Vs/Si1` and `Si2-Si3` decompose the same way.
  var claTokens = function (c) {
    return String(c || '').toUpperCase().split(/[-\/]/)
      .map(function (x) { return x.trim(); })
      .filter(Boolean);
  };
  // Resolve ONE token to a ladder index: exact rung, or a family's lowest rung.
  var claRung = function (tok) {
    var i = CLARITY.indexOf(tok);
    if (i >= 0) return i;
    return CLARITY_FAMILY[tok] != null ? CLARITY.indexOf(CLARITY_FAMILY[tok]) : -1;
  };
  // Ladder index for a grade, a family or a band. -1 when nothing resolves — which
  // is now a real answer ("not clarity-graded") rather than an oversight, and
  // `passes()` still skips its guard on it, so those stones stay findable.
  var claIndex = function (c) {
    var toks = claTokens(c);
    if (!toks.length) return -1;
    var lo = -1;
    for (var i = 0; i < toks.length; i++) {
      var r = claRung(toks[i]);
      if (r < 0) return -1;      // an unknown token poisons the whole value —
                                 // "VS/Opaque" must not quietly become VS2
      if (lo < 0 || r < lo) lo = r;      // ladder is worst->best, so lowest index
    }
    return lo;
  };
  // ⚠️ WHAT IS LEFT OFF THE LADDER, AND WHY IT IS NOT A GAP (owner rulings 2026-08-06):
  //   `Opaque` (25) is NOT A CLARITY GRADE — it is the correct descriptor for a black
  //       diamond, which the FL-I3 scale does not grade at all. It gets no rung and no
  //       chip row: black diamonds ARE fancy colour (GIA grades them "Fancy black"),
  //       their `color` already reads "Black", and `isFancy` already puts them in the
  //       Fancy color row — so they are findable there and a second control would be
  //       a duplicate. What they must NOT do is answer a clarity range they have no
  //       grade for, which is what `claWideOpen` in passes() now prevents.
  //   `Vq1` (2) is BAD DATA, not a grade: the serial is `VQ1DIAMOND-GEN:599051:G` and
  //       the product line leaked into the clarity field. Their real clarity is
  //       `VS-SI1`, in the feed's `desc` — a pull-side repair, not a ladder entry.

  // "Fancy Vivid Orange Yellow" -> "Yellow". The hue is the last word; the words
  // before it are intensity, which belongs on the card, not in a chip row that
  // would otherwise carry twelve near-duplicate labels for five actual colours.
  var fancyHue = function (c) {
    var w = String(c || '').trim().split(/\s+/);
    return w.length ? w[w.length - 1] : '';
  };

  // OFF-SCALE: a real colour grade that is not on the K-D ladder and is not fancy —
  // today just "U-V" on a single stone, tomorrow whatever Stuller sends next. It
  // must never be silently dropped and must never silently PASS: `passes()` skips
  // its guard on an unknown grade, so without this an off-scale stone matched at
  // every slider position. Instead these are excluded from the slider and listed as
  // their own chips, so the eight grades a shopper actually shops by keep the whole
  // track. Derived rather than enumerated, so a new grade needs no code change to be
  // handled safely — only to be given a nicer label.
  //
  // ⚠️ `colIndex`, NOT `COLOR.indexOf` — the whole point of the 2026-08-06 change.
  // With a bare indexOf every BAND ("G-H", "Ghi", "F+") answered -1 and landed here,
  // which is how 410 near-colourless stones came to sit under "warmer than K".
  //
  // ⚠️ WHAT IS LEFT HERE IS NOW ONLY A REAL OFF-LADDER DESIGNATION — L-Z, U-V, and
  // out-of-range bands like "L-M". A value that is neither grade nor designation is
  // read as a HUE by `isFancy` and shows in the Fancy color row instead. That is the
  // right default for a COLOUR field (a non-grade value there is overwhelmingly a
  // hue) but it does mean junk would present as a hue chip rather than a raw
  // "other" chip — the "Minute" case documented above landed in cut_grade, not
  // here. Either way it is surfaced and never silently passes the slider, which is
  // the property that matters.
  var isOffScale = function (c) {
    c = String(c || '').trim();
    return !!c && !isFancy(c) && colIndex(c) < 0;
  };

  // Bucketing the tinted end under one **L–Z** chip is deliberate: pinning the row
  // to whichever sub-range we happen to hold today would rename itself every time
  // stock turned over, and a shopper looking at the tinted end is not shopping "U-V
  // specifically", they are shopping "warmer than K". One chip that stays put reads
  // better and stays true. The CARD still prints the stone's exact grade.
  //
  // ⚠️ ONLY REAL D-Z DESIGNATIONS ARE BUCKETED — anything unrecognised keeps its own
  // raw-valued chip rather than being labelled "L–Z", which would state something
  // false. (BELOW_K itself is declared above isFancy, which needs it.)
  var offKey = function (c) {
    c = String(c || '').trim();
    return BELOW_K.test(c) ? 'L-Z' : c;
  };

  // Every cut wording a lab can hand us, mapped to its rung in CUT above. This is
  // for FILTERING only — the card still prints the lab's own word, because showing
  // "Super Ideal" beside a GCAL report that says "8X" would misquote the report.
  //
  // ⚠️ A WORDING MISSING FROM HERE IS NOT FILTERED OUT, IT IS FILTERED PAST:
  // `passes()` reads `r._cut != null`, so an unmapped grade gives undefined, skips
  // the guard and matches at EVERY slider position. `verify_diamond_assets.py`
  // fails on any cut grade the shards carry that this map does not, which is the
  // only place that can be caught — the builder's own shards have no such check,
  // and they currently hold 2 stones whose cut_grade is "Minute", a FLUORESCENCE
  // value sitting in a cut field. It is deliberately NOT mapped: inventing a rung
  // for it would launder a data error into a grade.
  var CUT_MAP = {
    'fair': 0, 'good': 1, 'very good': 2,
    'excellent': 3, 'ex': 3,
    'ideal': 4,
    '8x': 5, 'super ideal': 5
  };

  var title = function (s) {
    return String(s || '').toLowerCase().replace(/(^|\s)\w/g, function (m) { return m.toUpperCase(); });
  };
  var caret = function () {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('width', '10'); s.setAttribute('height', '6'); s.setAttribute('viewBox', '0 0 10 6');
    s.innerHTML = '<path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1"/>';
    return s;
  };

  /* 🚨 A FACET IS NOW A PANEL ROW (owner, 2026-08-12). Every call site in this file
     is unchanged — the signature is identical and the `open` flag is simply ignored,
     because the gemstone panel this now matches has no collapsed state: label on the
     left, controls on the right, always visible.

     Converting the BUILDER rather than the ten call sites is deliberate. The rows
     have to agree with each other and with /collections/all-gemstones; ten
     hand-converted call sites is ten chances for one of them to keep a chevron.

     `help` is a sr-facet-help.js key (2026-09-03). It was `hint`, a sentence drawn as
     the label's own sub-line (`.sb__label em`) and then suppressed (owner,
     2026-09-02: the rail must match the gemstone search row for row, and the
     six-line Cut note set the row's height). The copy now lives ONCE on the
     engine and is drawn as a tap-to-open panel beside the label — on this rail
     and on the gemstone search, from the same map. */
  // ⚠️ `mod` IS AN OVERRIDE, NOT THE NORM. Width is DERIVED from content below
  // precisely so a row cannot land in the wrong track by omission; the one case that
  // derivation cannot see is a row that holds no scroller and still has no partner —
  // `.rb-rail` is a two-track grid, so an odd number of narrow rows always orphans
  // one, and where the hole falls is a judgement about the panel rather than about
  // the row. Pass it only for that.
  function facet(name, open, body, help, mod) {
    var kids = [];
    (body || []).forEach(function (n) { if (n) kids.push(n); });
    // A row holding a TILE SCROLLER spans both panel columns; a slider or a short
    // pill list takes one. Decided from the content rather than passed in by each
    // call site, so a row cannot end up in the wrong track by omission — which is
    // how the shape row, the widest thing on the panel, would have been squeezed
    // into half the width the first time someone added a facet above it.
    var wide = mod === 'wide' || kids.some(function (n) {
      return n && n.classList && n.classList.contains('sb__scroll');
    });
    /* THE HELP PANEL, NOT A SUB-LINE. `help` names an entry in sr-facet-help.js and
       the button lands INSIDE `.sb__label`, after the label text, whichever of
       the three row shapes below is built — so a row cannot lose its help by
       taking a different branch. Null or an unknown key draws nothing. */
    var withHelp = function (r) {
      // Guarded: a cached previous ring-builder.js has no attachHelp (see diamond-browse.js row()).
      if (help && RB.attachHelp) RB.attachHelp(r.querySelector('.sb__label'), help);
      return r;
    };
    /* 🚨 A SLIDER ROW IS THE REFERENCE'S ROW, NOT `RB.panelRow` (2026-09-02). The
       gemstone search wraps its slider as `.sb__row.sb__row--tight` > `.sb__label`
       + `.sb__ranged` > control (RB.rangeRow), and stone-browse.css keys the row's
       tighter padding and the track's width on exactly those classes — through
       panelRow's plain `div[style=min-width:0]` the same control renders at a
       different height. Two shapes arrive here, and the call sites keep their
       `facet('Carat', true, [...])` form either way, because
       verify_builder_colored_rail.py [7] reads the rail's ORDER off those calls:
         • a bare control from RB.rangeControl (the graded 4C ladders) — wrapped in
           the reference row here, label from `name`, the help button (see above);
         • a whole row from RB.contRow (carat, price) — passed through, since a
           continuous axis needs SRScale to size its ladder and RB.contRow is the
           one place on the store that resolves it.
       `wide` still applies to both, or the Price row on the diamond tab sits in
       the left track of `.rb-rail`'s grid with a hole beside it. */
    if (kids.length === 1 && kids[0].classList && kids[0].classList.contains('sb__row')) {
      if (wide) kids[0].classList.add('sb__row--wide');
      return withHelp(kids[0]);
    }
    if (kids.some(function (n) { return n.classList && n.classList.contains('sb-range'); })) {
      return withHelp(el('div', { class: 'sb__row sb__row--tight' + (wide ? ' sb__row--wide' : '') }, [
        el('span', { class: 'sb__label' }, [document.createTextNode(name)]),
        el('div', { class: 'sb__ranged' }, kids)
      ]));
    }
    return withHelp(RB.panelRow(name, null, kids, wide ? 'sb__row--wide' : null));
  }

  /* ------------------------------------------------------------- the page */
  /* 🚨 MOUNT RUNS ON EVERY FILTER NOW, NOT ONCE PER LOAD (2026-09-17; owner 2026-09-16: "yes, let's proceed with the
     fixes" — no reload on a filter tap, as on the collection pages). ring-builder-pages.js hands this page to
     assets/sr-client-filters.js, which writes the address without a load and calls mount() again. So everything
     here that must happen once per page is guarded — the density buttons, the side-stone banner, the sort closer and
     the drawer's page-wide listeners — and a redraw keeps an open drawer open. `token` (absent on the page load)
     says whether a newer filter has overtaken this draw, and `token.calm` holds the panel while the shopper scrolls. */
  var FIRST = { first: true, calm: function (v) { return v; }, stale: function () { return false; } };
  /* 🚨 THE EARRING GRID OPENS WHERE THE STOCK IS, AND READS IT FROM THE DATA (audit M068; owner 2026-09-24: "open the
     gemstone step on the type and origin that has stock, honest counts"). Started at the gemstone step, the earring
     builder showed one natural sapphire, "Diamonds 0", "Lab-Grown 0" and gray Emerald and Pear tiles while a lab diamond
     was stocked in each of those shapes (09-24 build: lab diamonds in Oval, Emerald and Pear; one natural sapphire in
     Oval). `earringKinds` (rb-stones-meta.js, rebuilt every night from earring_release.earring_counts) counts exactly
     the stones this grid lists before a mounting is chosen — allowlisted, earring-tagged, available, calibrated — so
     nothing below names a stone, a shape's stock or a formation: the slow-stones rule can change the answer any night.
     `earringCount` is what an earring page lists for a shape when the shopper has not picked a stone type: the page
     opens on diamonds where it has any, else on colored stones (the auto-switch in mount), so it is that tab's count. */
  function earringKind(sh, o) {
    var k = ((window.RB_STONES_META || {}).earringKinds || {})[sh + '|' + (o === 'lab' ? 'lab' : 'nat')];
    return { d: (k && k.d) || 0, c: (k && k.c) || 0 };
  }
  function earringCount(sh, o) { var k = earringKind(sh, o); return k.d || k.c; }
  // Why an earring shape tile is gray on the page of formation `o`: its shape is stocked in the other formation only.
  // ONE copy of the words (owner 2026-09-25, answer 7), for the tile's tooltip (n = 1) and for the line under the
  // Shape row that names the gray tiles (n = how many), which is the one a phone can read.
  function earringOnly(o, n) {
    return 'Only ' + (o === 'lab' ? 'natural' : 'lab-grown') + ' stones in ' + (n > 1 ? 'these shapes' : 'this shape');
  }
  // The shape an earring grid opens on when nothing chose one: Oval, as before, while either formation stocks it, else
  // the first shape (SHAPES order) either one stocks. The same answer on both formation pages, so a Formation count
  // describes the page its link opens.
  function earringShape() {
    var list = ['OVAL'].concat(SHAPES);
    for (var i = 0; i < list.length; i++) {
      if (earringCount(list[i], 'lab') || earringCount(list[i], 'nat')) return list[i];
    }
    return 'OVAL';
  }
  function mount(root, build, origin, token) {
    token = token || FIRST;
    var q = new URLSearchParams(location.search);
    var earringRelease = RB.kind(build) === 'earring';
    var setting = build.style ? RB.setting(build.style) : null;
    /* ⚠️ `for` IS A MODE, NOT BUILD STATE, so it is read here and deliberately kept
       OUT of RB.FIELDS: it describes what this visit to the grid is doing, not
       anything about the ring. In FIELDS it would ride along on every link the
       shopper follows afterwards and turn the ordinary centre grid into a side
       picker two steps later.
       ⚠️ It is honoured only when the style actually HAS a side seat for this
       centre — `?for=side` typed onto a solitaire is a URL nobody can satisfy, and
       falling through to the normal grid is better than an empty one that never
       explains itself. */
    /* 🚨 RESOLVED ONCE, BECAUSE THE SHAPE BELOW DEPENDS ON IT. This was three
       separate `RB.sideSeat` calls (here, the pool block, the count line) and the
       shard the page loads has to be derived from the seat BEFORE any of them —
       so a fourth call would be a fourth chance for them to disagree about which
       seat this visit is filling. */
    var sideSeat = (q.get('for') === 'side' && setting)
      ? RB.sideSeat(setting.style, build.seat) : null;
    var forSide = !!sideSeat;
    // ONE stone on a second-stone design gets the owner's words (RB.secondStone, ring-builder.js). Guarded: for a
    // few minutes after a release this file can meet the engine's previous copy, which has no such function.
    var secondSide = !!(forSide && RB.secondStone && RB.secondStone(setting.style, sideSeat) &&
                        RB.pairQty(build) === 1);
    var rail = root.querySelector('[data-rb-rail]');
    var grid = root.querySelector('[data-rb-grid]');
    var countEl = root.querySelector('[data-rb-count]');

    /* DENSITY — the reference's own control (owner, 2026-09-02: "4 per row by
       default + the density options"). The four buttons are static markup in
       sections/ring-builder.liquid wearing the theme's own glyphs; the KEY is the
       theme's `productDesktopViewCols`, so a shopper who chose 3-up on
       /collections/sapphires arrives here 3-up and back again. Same contract as
       diamond-browse.js: `--db-cols` drives the column count, `data-db-cols` the
       nine-up rule that hides the card body (CSS cannot select on a custom
       property's VALUE). Wired before any shard loads — the buttons and the grid
       element already exist, and a visibly live control must not sit inert. */
    var DENSE_KEY = 'productDesktopViewCols', DENSE_OK = ['2', '3', '4', '9'];
    var denseBtns = root.querySelectorAll('[data-rb-dense] .db-dense__b');
    function applyDensity(cols, persist) {
      if (DENSE_OK.indexOf(cols) < 0) cols = '4';
      if (grid) {
        grid.style.setProperty('--db-cols', cols);
        grid.setAttribute('data-db-cols', cols);
      }
      Array.prototype.forEach.call(denseBtns, function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-db-cols') === cols));
      });
      if (persist) { try { localStorage.setItem(DENSE_KEY, cols); } catch (e) { /* private mode */ } }
    }
    if (denseBtns.length) {
      var savedDense = '4';
      try { savedDense = localStorage.getItem(DENSE_KEY) || '4'; } catch (e) { /* private mode */ }
      applyDensity(savedDense, false);
      // ⚠️ ONCE PER PAGE: these buttons are the section's and outlive every redraw (see mount's note).
      Array.prototype.forEach.call(denseBtns, function (b) {
        if (b.__rbDense) return;
        b.__rbDense = true;
        b.addEventListener('click', function () {
          applyDensity(b.getAttribute('data-db-cols'), true);
        });
      });
    }
    // The two population rows above the panel. Server-rendered by
    // sections/ring-builder.liquid — Formation's links must ship in the HTML (they
    // are the only crawlable route to the lab pages) and its counts must not, so
    // the markup is Liquid's and the numbers are ours.
    var kindsEl = root.querySelector('[data-rb-kinds]');

    /* 🚨 A SIDE STONE'S SHAPE IS THE SIDE SEAT'S, AND READING THE CENTRE'S KILLED
       88% OF THIS FEATURE ON ARRIVAL (owner report 2026-08-19).
       `shape` decides which SHARD is fetched, and every side seat on the pilot
       style is OVAL while `build.shape` is the CENTRE shape. `RB.fits` opens with
       `stone.shape !== s.shape -> false`, so on any non-oval centre the grid loaded
       a shard in which NOTHING can fit an oval side seat. Measured by executing the
       shipped engine over all 135 centre seats of style 127205:
         ASSCHER 8, CUSHION 12, EMERALD 18, HEART 8, MARQUISE 17, PEAR 22,
         ROUND 22, SQUARE 12  ->  119 of 135 seats (88%) showed ZERO on BOTH tabs.
       Only the 16 OVAL centres worked, and only because the centre shape happened
       to equal the side shape. With the side shape loaded, all 135 have stock.
       ⚠️ IT LOOKED LIKE THE TAB BUG AND IT IS NOT. A dead grid also drops the price
       slider to its empty-pool fallback ($0–$1,000, `pMax = prices.length ? … : 1000`)
       and the carat slider to 0.20–12.60, which reads exactly like an inherited
       filter that emptied the list. It is the symptom, not the cause: `RB.href`
       serialises only `FIELDS`, so no filter is ever carried in here — verified on
       the live page, `activeFilterChips: []` beside a grid of 0.
       ⚠️ `q.get('shape')` MUST NOT WIN HERE. The build carries the centre shape in
       the URL on every link, so honouring it is precisely the defect. */
    var shape = forSide ? RB.parseSeat(sideSeat.spec).shape
      : (q.get('shape') || build.shape ||
         (setting && setting.shapes.indexOf('ROUND') >= 0 ? 'ROUND' : setting && setting.shapes[0]) || (earringRelease ? earringShape() : 'ROUND'));
    // A PEARL seat can never hold a diamond, so defaulting a pearl build to the
    // Diamonds tab greeted the shopper with "0 natural diamonds that fit your
    // setting" and an empty grid — the setting looked broken when it was simply
    // the wrong tab. An explicit ?type= still wins, so the toggle keeps working.
    var kind = q.get('type') ? (q.get('type') === 'color' ? 'color' : 'diamond')
                             : (shape === 'PEARL' ? 'color' : 'diamond');

    /* 🚨 AN EARRING GRID WITH NOTHING IN THIS SHAPE OPENS THE OTHER FORMATION (audit M068). The two entry links send an
       earring shopper to the lab grid (RB.stonesPage's default for an earring build with no formation), but any night's
       stock can empty a shape there, and older links still open the natural grid. So on the PAGE LOAD only (a filter
       redraw never moves the shopper), with no stone type and no formation chosen (`type` / `origin` in the address;
       the Formation links below name the page they open), a formation with no stone in this shape hands over to the
       other one when that one has some. It can turn a dead end into a live page and never moves a live one; the grid's
       own auto-switch then picks the stone type. `replace`, so Back does not return to the empty page. The path is the
       Formation anchor's own href, as everywhere in this file. */
    var eHere = origin === 'lab' ? 'lab' : 'nat', eThere = eHere === 'lab' ? 'nat' : 'lab';
    if (earringRelease && token.first && !forSide && !q.get('type') && q.get('origin') !== eHere &&
        !earringCount(shape, eHere) && earringCount(shape, eThere)) {
      var eLink = root.querySelector('[data-rb-origin-link="' + eThere + '"]');
      if (eLink) {
        var eTo = new URLSearchParams(location.search);
        eTo.set('origin', eThere);
        location.replace(eLink.getAttribute('href').split('?')[0] + '?' + eTo.toString());
        return Promise.resolve();
      }
    }

    function apply(patch) {
      var n = new URLSearchParams(location.search);
      Object.keys(patch).forEach(function (k) {
        if (patch[k] == null) n.delete(k); else n.set(k, patch[k]);
      });
      // In place once ring-builder-pages.js has handed this page to sr-client-filters.js; a page load otherwise.
      if (root.__srClient && root.__srClient.go('?' + n.toString())) return;
      location.search = n.toString();
    }
    var num = function (k, d) { var v = parseFloat(q.get(k)); return isNaN(v) ? d : v; };

    /* ═══ THE PAGE HAS TO SAY IT IS PICKING SIDE STONES ═══════════════════════
       Owner report 2026-08-19: "it looks identical to the centre-stone step" — and
       it did, down to the H1. `?for=side` reuses this whole grid deliberately (a
       second stone page would be a second copy of a 1,500-line surface), so the
       ONLY thing that differed was one clause in the count line, below the fold on
       a phone. Measured on the live page before this fix: H1 "Choose a gemstone",
       step bar "2 Choose a gemstone [ACTIVE]", the same rail, the same cards. The
       owner read it as the centre-stone page, which is the correct reading of what
       it said.

       Written SYNCHRONOUSLY, before the shard fetch, so the page never paints one
       frame claiming to be step 2. The step bar is corrected in
       ring-builder-pages.js — a heading that says "side stones" over a bar that
       says "2 Choose a gemstone" is two answers to one question. */
    if (forSide) {
      var _pieceWord = RB.KINDS[RB.kind(build)].label;
      var _seatWord = RB.sideSeatLabel ? RB.sideSeatLabel(sideSeat.spec, null, build.style, sideSeat.set) : RB.seatLabel(sideSeat.spec);
      var _n = sideSeat.n || 2;
      var _h1 = root.querySelector('h1');
      if (_h1) _h1.textContent = secondSide ? 'Choose your second stone' : 'Choose your side stones';
      var _sub = root.querySelector('[data-rb-subheading]');
      /* ⚠️ SHORT, BECAUSE THE BANNER BELOW ALREADY STATES THE SEAT. The first cut
         repeated "2 × Oval 5.00 × 3.00 mm" here, in the banner AND in the count line,
         and on a phone the three of them pushed the first stone to 882px against the
         centre grid's 641 — a browse surface whose first result is a screen and a
         half down. This sentence keeps only the part neither of the others carries:
         WHY the list is short. */
      /* 🚨 NO SUBHEADING IN SIDE MODE (owner, 2026-08-20: "too wordy"). The banner
         states the seat, the count line under the grid states it again with the
         quantity, and the H1 says what is being chosen — a fourth sentence explaining
         WHY the list is short reads as an apology. Hidden, not emptied, so it takes
         its margin with it. */
      if (_sub) _sub.hidden = true;
      /* 🚨 THERE IS NO SKIP LINK, AND ITS REMOVAL IS THE POINT. It offered "Keep the
         matched diamonds instead" for one day — a route to a ring that cannot be
         made, because this mounting has no diamonds in it to keep. The only way
         back is to a build that still needs this step, and review will say so. */
      /* 🚨 STEP 3 NEEDS A WAY FORWARD OR IT IS A CUL-DE-SAC. Until the step became a
         real stop the only exits were a card (which picks) and "back". Now that the
         shopper is routed HERE after the centre stone, Continue is the whole point:
         it means "the default is fine". It is added only once `build.side` is set —
         with nothing chosen it would lead to a review that refuses. */
      var _cont = el('a', { class: 'btn btn-primary rb-sideban__go',
        href: RB.href(RB.pages.review, build),
        // ⚠️ Not "Continue to your ring" — this IS the ring, and the shopper is inside
        // building it. The button just moves them to the last step.
        text: 'Continue →' });
      /* 🚨 THE TWO LINKS POINTED AT THE SAME PAGE, WHICH IS WHY NEITHER READ SENSIBLY
         (owner, 2026-08-20). "Continue to your ring" and "← Back to your ring" were
         both `RB.pages.review` — forward and back to one destination, described twice.
         Back now goes where back means: the CENTRE stone, the step before this one.
         ⚠️ It must not carry `for: 'side'`; that is the mode flag, and leaving it on
         would send them straight back to the picker they are trying to leave.
         `RB.href` serialises FIELDS only and `for` is not one, so it drops by
         construction — the one place that behaviour is exactly what is wanted. */
      var _acts = [_cont, el('a', { class: 'rb-sideban__a',
        // "your center stone" is the DETAIL page of the stone they chose; the grid is
        // the fallback only for a build that has none (Codex, 2026-09-02)
        href: RB.href(build.stone ? RB.pages.stone : RB.stonesPage(build), build),
        text: '← Back to your center stone' })];
      var _ban = el('div', { class: 'rb-sideban' }, [
        /* ⚠️ "Step 3 of 4", NOT "Optional upgrade" — the eyebrow said the latter for
           one day and it was a false claim about the product, not a tone choice: the
           mounting ships bare (`SetWith` empty on 24 of 24 sampled ACTIVE variants),
           so a ring bought without these arrives with two holes in it. */
        el('p', { class: 'rb-sideban__k', text: 'Required' }),
        /* ⚠️ THE TRAILING CLAUSE IS STRIPPED, AND ONLY THE TRAILING ONE. The pilot
           product is titled "… Setting — Choose Your Side Stones" (it had to be, to
           avoid colliding with the Semi-Set product of the same design), so the
           banner read "Side stones for your Three-Stone Engagement Ring Setting —
           Choose Your Side Stones" — the instruction repeated inside the thing it
           describes. `RB.settingName` cannot do this: it strips the word "Setting",
           which is a rule about every mounting in the catalogue, and an em-dash tail
           is a rule about a title. Any future mounting whose name genuinely needs
           its tail keeps it everywhere else. */
        /* ⚠️ THE SEAT IS THE HEADLINE, NOT THE SETTING'S NAME. This led with "Side
           stones for your <full design title>" — up to 78 characters of a name the
           step bar is already showing two rows above, and the one thing making the
           panel wide. What the shopper cannot get elsewhere on this screen is the SIZE
           and COUNT of what they are buying. */
        el('p', { class: 'rb-sideban__t', text: secondSide ? _seatWord : _n + ' × ' + _seatWord }),
        el('p', { class: 'rb-sideban__s rb-sideban__from', text: '' }),
        el('div', { class: 'rb-sideban__acts' }, _acts)
      ]);
      /* Above the population rows, which is the first thing that reads as the
         centre step. `[data-rb-types]` is absent on a page rendered without them,
         so fall back to the rail rather than dropping the banner. */
      var _anchor = root.querySelector('[data-rb-types]') ||
                    root.querySelector('[data-rb-rail]');
      // A redraw replaces the banner rather than adding a second one (see mount's note).
      var _was = root.querySelector('.rb-sideban');
      if (_was && _was.parentNode) _was.parentNode.removeChild(_was);
      if (_anchor && _anchor.parentNode) _anchor.parentNode.insertBefore(_ban, _anchor);
      root.classList.add('rb--side');
      /* The banner is built synchronously so the page never paints one frame claiming
         to be step 2 — but the default and the price floor are async, so they are
         written back into it when they land. */
      /* 🚨 CONTINUE STAYS HIDDEN UNTIL THE CARRIED SIDE IS SETTLED. It was shown
         synchronously on the strength of any truthy vid, i.e. before the resolve had
         checked that the stone exists or fits (Codex, fourth pass, 2026-09-02). It is
         set from `build.side` once the chain below has settled and refilled. */
      _cont.hidden = true;
      /* 🚨 ONE SETTLEMENT, AND THE GRID WAITS ON IT TOO. The shopper may have changed
         the centre stone since choosing sides, and the old `side` rides along — resolve
         it, drop it if it vanished or no longer fits, and let the applier give THIS seat
         its own default. `root.__sideSettled` is that promise; the grid draw further
         down chains off it, because drawing off its own `ensureDefaultSide` (a no-op
         while a stale vid is set) painted an unticked grid that never re-marked the
         default the settlement then chose. */
      root.__sideSettled = (build.side
        ? RB.resolveStone({ stone: build.side, shape: null, origin: build.origin })
            .then(function (st) { RB.settleSide(build, st); })   // null = vanished: drop it
        : Promise.resolve()
      ).then(function () {
        return RB.ensureDefaultSide(build);
      });
      root.__sideSettled.then(function () {
        _cont.hidden = !build.side;
        _cont.setAttribute('href', RB.href(RB.pages.review, build));
        return RB.sideFloors(build, sideSeat);
      }).then(function (fl) {
        var f = root.querySelector('.rb-sideban__from');
        if (!f || !fl) return;
        /* Each route named with its own floor, so the natural/lab gap is on screen
           rather than discovered by pressing Lab-Grown. The COLOURED floor is
           computed too, never assumed — it was briefly hardcoded at $55 a stone,
           which is today's cheapest and not a constant. */
        /* ONE LINE. The floors matter — the owner asked for a starting price and for
           lab to be visible without pressing anything — but three clauses of
           "<species> from $x" is a paragraph. Cheapest first; diamonds qualified by
           formation because that is the gap worth seeing ($170 against $1,150). */
        var d = [];
        if (fl.labDia) d.push(money(fl.labDia) + ' lab');
        if (fl.natDia) d.push(money(fl.natDia) + ' natural');
        var bits = [];
        if (fl.colour) bits.push('From ' + money(fl.colour) + (secondSide ? '' : ' the set'));
        if (d.length) bits.push('diamonds from ' + d.join(', '));
        f.textContent = bits.join(' · ');
      });
    }

    // Skeletons on the page load only: a later filter keeps the stones on screen, dimmed, until the new ones are
    // drawn, so a shopper scrolled into the results is not dropped by a grid that suddenly shrinks.
    if (token.first) {
      grid.innerHTML = '';
      for (var i = 0; i < 6; i++) grid.appendChild(el('div', { class: 'rb-skel' }));
    }

    /* 🚨 THE DEFAULT IS APPLIED BEFORE THIS GRID DRAWS, NOT AFTER. Step 3 is a real
       stop now (owner, 2026-08-20: "the third step should not be skipped"), so the
       shopper arrives here with a diamond already chosen — and the grid has to KNOW
       that to tick the right card and to let Continue mean "keep this one". Applied
       after the draw, the page would paint an unselected grid and then change under
       them. The shard `ensureDefaultSide` loads is the same one drawn below, so the
       cache makes this free. */
    /* ⚠️ OFF THE SETTLEMENT PROMISE, NOT A SECOND APPLIER CALL. A stale carried vid
       makes `ensureDefaultSide` return at once, so a draw chained off it painted with
       no card ticked and the later replacement never re-marked one. */
    return (forSide ? (root.__sideSettled || RB.ensureDefaultSide(build)) : Promise.resolve(null))
      .then(function () {
        /* A side seat's pool is every shape it takes (RB.loadSideRows: a Radiant beside an Emerald seat's own
           stones, owner 2026-10-06); an engine older than that loads the seat's own shard, as before. */
        if (forSide && RB.loadSideRows) return RB.loadSideRows(sideSeat, origin === 'lab' ? 'lab' : 'nat', build.style);
        return RB.loadShard(shape, origin === 'lab' ? 'lab' : 'nat').then(function (sh) { return RB.rows(sh); });
      })
      .then(token.calm)
      .then(function (loaded) {
      // A newer filter has overtaken this draw while its stones loaded: that draw wins, this one paints nothing.
      if (token.stale()) return;
      var all = loaded.filter(function (r) {
        return r.available && (!earringRelease || RB.eligibleFor(r, 'earring'));
      });
      all.forEach(function (r) {
        r._cut = CUT_MAP[String(r.cut || '').toLowerCase()];
        r._col = colIndex(r.color);          // letters AND bands — see colIndex
        r._cla = claIndex(r.clarity);        // casing, families AND bands
        r._isD = r.species === 'Diamond';
        r._fancy = isFancy(r.color);
        r._hue = r._fancy ? fancyHue(r.color) : null;
        // The shard groups pearls under shape PEARL (build_builder_assets.stone_shape
        // maps gem_type "Pearl" there, because a pearl's own stone_shape is "Round"
        // while the mounting calls its seat PEARL). `species` is the title's last
        // word, so a pearl reads "Pearl" — but the SHARD is the reliable signal.
        r._isPearl = shape === 'PEARL' || r.species === 'Pearl';
        r._lw = (r.l && r.w) ? r.l / r.w : null;
      });
      // An earring page opens on the stone type it can LIST: a pair needs calibrated stock, the same stones `earringKinds`
      // counts, so the Formation and Shape counts below describe exactly the tab a link opens (audit M068).
      if (earringRelease && !q.get('type') && !all.some(function (r) { return r._isD && RB.pairable(r); }) && all.some(function (r) { return !r._isD && RB.pairable(r); })) kind = 'color';
      var pool = all.filter(function (r) { return kind === 'diamond' ? r._isD : !r._isD; });
      // side-eligible counts per tab, filled in side mode only; null everywhere else
      var sideCount = null;

      // FIT IS A REAL FILTER, NOT A CAPTION. The count used to append the words
      // "that fit your setting" while nothing was filtered — so it stated
      // something false, and a stone the mounting cannot hold was one click from
      // the cart. A stone qualifies if ANY seat of the chosen style can take it
      // (the seat then snaps to that stone; see RB.bestSeat), which is the only
      // reading of "fits your setting" that is true.
      /* ── SIDE-STONE MODE ────────────────────────────────────────────────
         `?for=side` reuses this whole grid — every filter, the compare tray, the
         sort — to pick the SIDE stones instead of the centre. It is a mode rather
         than a new page deliberately: a second stone page would be a second copy of
         a 1,500-line grid, and the two would agree until somebody edited one.

         The pool is `RB.sideCandidates`, which is a STRICTER gate than the centre's,
         in two ways that both matter:
           · it fits the SIDE seat (`sides[build.seat]`), not any seat of the style —
             a side seat is one size, so `bestSeat`'s "any seat will do, we'll snap"
             reading is wrong here; the mounting has already been chosen;
           · it requires CALIBRATED stock, because both sides are one stone ordered
             twice and a one-of-one cannot be supplied twice.
         With no mounting chosen there is no side seat, so the mode cannot be entered
         and the URL falls through to the ordinary centre grid. */
      if (forSide && setting) {
        /* 🚨 BOTH TABS ARE COUNTED, NOT JUST THE ONE ON SCREEN — and that is what
           makes the tab CHOICE legible instead of a guess. The population row reads
           the shard REGISTRY (`kcount`), which on a side seat is the count of every
           oval stone we stock: it printed "Diamonds 78" over a grid of 4. A shopper
           cannot tell a narrow seat from a broken page when the number above the
           grid disagrees with the grid by an order of magnitude. `sideCount` is the
           side-eligible figure for each tab and the row prints that instead, so
           "Diamonds 4 · Colored stones 49" explains itself and the 49 are visibly
           one click away. */
        var _byKind = {
          diamond: RB.sideCandidates(all.filter(function (r) { return r._isD; }),
                                     sideSeat, setting.style),
          color: RB.sideCandidates(all.filter(function (r) { return !r._isD; }),
                                   sideSeat, setting.style)
        };
        sideCount = { diamond: _byKind.diamond.length, color: _byKind.color.length };
        /* 🚨 OPEN ON THE TAB THAT HAS STOCK. The grid defaults to DIAMONDS, and a
           side seat is small and calibrated — measured on the pilot's 5.00 x 3.00 mm
           oval side: 4 stones fit and NONE of them is a diamond, because we stock no
           calibrated 5x3 oval diamond. So the shopper pressed "Choose side stones"
           and landed on "0 natural diamonds" with an empty grid, which reads as a
           broken builder rather than as the wrong tab.

           This is the same rule the PEARL default already applies one line up, for
           the same reason and with the same shape: it only fires when the shopper
           has NOT chosen a tab (`?type=` still wins), and only when the other tab
           genuinely has something — so it can move a dead end to a live one and can
           never move a live one anywhere. */
        /* ⚠️ THE FLIP IS EVALUATED ON THE SIDE-ELIGIBLE POOL, WHICH IS NOW THE
           POOL THAT IS ACTUALLY SHOWN. Nothing filters it further on arrival — no
           filter is carried into side mode, and every slider bound is derived from
           this pool, so `rows` equals `pool` on the first paint. Re-testing after
           `passes()` would therefore change no outcome here and would introduce one
           that is worse: a tab that swaps itself out from under a shopper who just
           moved a slider. It fires only while they have not chosen a tab, so it can
           turn a dead end into a live one and can never move a live one.
           ⚠️ DIAMONDS STAY THE DEFAULT WHERE THEY HAVE STOCK (owner rule: nothing
           renders empty, diamonds by default) — the mounting ships with diamond
           sides, so the diamond tab is the ring as quoted. */
        if (!_byKind[kind].length && !q.get('type')) {
          var _flip = kind === 'diamond' ? 'color' : 'diamond';
          if (_byKind[_flip].length) kind = _flip;
        }
        pool = _byKind[kind];
      } else if (setting) {
        pool = pool.filter(function (r) { return !!RB.bestSeat(setting.style, r); });
      } else {
        // 🚨 NO MOUNTING YET, SO FIT CANNOT BE THE FILTER — but the pool still
        // must not offer a stone this wave can never use. This is the stone-first
        // entry (owner decision 2026-08-05): the shopper reaches the gemstone
        // grid before choosing anything to set it in, so the only honest gate is
        // the stone's own builder eligibility, which is precisely what the three
        // `*-builder-stone` tags record and what the shard's `w` column now
        // carries. Without it a ring shopper starting with a stone would be shown
        // the 225 earring-only stones that fit no ring seat in the catalogue, and
        // every one of them would dead-end at step 2.
        // NOTE `RB.kind(build)`, not the local `kind` — that one is
        // diamond-vs-colour, a different axis entirely.
        // ⚠️ IN SIDE MODE THE ROLE IS THE WAVE. Filtering by `RB.kind(build)` here
        // would strip every side-only stone — they carry the side bit and none of
        // the centre bits, on purpose — leaving the side picker exactly as narrow as
        // it was before the tagger existed.
        var wave = forSide ? 'side' : RB.kind(build);
        pool = pool.filter(function (r) { return RB.eligibleFor(r, wave); });
      }

      // 🚨 A PAIRED BUILD MUST ONLY EVER SEE PAIRABLE STOCK. This file had NO
      // knowledge of the wave at all — no RB.kind, no RB.pairable, no pairQty —
      // while ring-builder.js:69 and :566 both asserted that "the earring stone
      // step is filtered on exactly that". It was not. The only gate was the
      // addToCart guard, which fires AFTER the shopper has chosen a stone, opened
      // its detail page and reached review, and then refuses with "one of a kind"
      // — a dead end at the last step instead of a stone never offered.
      // A comment claiming a filter exists is not a filter.
      //
      // qty > 1 only ever means earrings today, but it is read from RB.pairQty so
      // a fourth wave that pairs needs nothing here.
      var pairQty = RB.pairQty(build);
      if (pairQty > 1) {
        pool = pool.filter(RB.pairable);
      }

      // bounds come from the pool, so a slider never offers an empty span
      var prices = pool.map(function (r) { return r.price; });
      var carats = pool.map(function (r) { return r.carat; }).filter(Boolean);
      var pMin = prices.length ? Math.floor(Math.min.apply(null, prices)) : 0;
      var pMax = prices.length ? Math.ceil(Math.max.apply(null, prices)) : 1000;
      var cMin = carats.length ? Math.min.apply(null, carats) : 0.2;
      var cMax = carats.length ? Math.max.apply(null, carats) : 12.6;

      var st = {
        price: [num('pmin', pMin), num('pmax', pMax)],
        carat: [num('ctmin', cMin), num('ctmax', cMax)],
        col: [parseInt(q.get('colmin') || 0, 10), parseInt(q.get('colmax') || COLOR.length, 10)],
        cla: [parseInt(q.get('clmin') || 0, 10), parseInt(q.get('clmax') || CLARITY.length, 10)],
        cut: [parseInt(q.get('cutmin') || 0, 10), parseInt(q.get('cutmax') || CUT.length, 10)],
        species: (q.get('sp') || '').split(',').filter(Boolean),
        // The COLORED rail's hue facet (`custom.gem_color`). Multi-select and
        // absence-means-all, the same contract as `species` and `fancy` — never a
        // sentinel value, which is the rule the shape row and formation both learned.
        hue: (q.get('hue') || '').split(',').filter(Boolean),
        fancy: (q.get('fancy') || '').split(',').filter(Boolean),
        // The supplier quality ladder on calibrated stones, plus the one-of-a-kind
        // bucket. Multi-select, absence-means-all — the same contract as `hue`.
        quality: (q.get('quality') || '').split(',').filter(Boolean),
        // The other three dropdowns, same contract and the same param NAMES the
        // gemstone search uses, so a URL copied from one rail works on the other.
        country: (q.get('country') || '').split(',').filter(Boolean),
        treatment: (q.get('treatment') || '').split(',').filter(Boolean),
        cutstyle: (q.get('cutstyle') || '').split(',').filter(Boolean),
        sort: q.get('sort') || 'best'
      };
      // "Has the shopper expressed a colour preference at all?" — the slider sitting
      // at its full span means no. It decides whether fancy stones belong in an
      // unfiltered list (they do) or in a narrowed one (they do not): asking for
      // D–F is asking for COLOURLESS, and answering it with a Fancy Vivid Blue
      // would be answering a different question.
      var colWideOpen = st.col[0] === 0 && st.col[1] >= COLOR.length;
      var claWideOpen = st.cla[0] === 0 && st.cla[1] >= CLARITY.length;

      /* ---------------- rail ---------------- */
      rail.innerHTML = '';

      // Only shapes THIS setting can hold are enabled. Disabled, never hidden.
      /* A shape whose every shard holds side stones only (meta `sideOnly`: the Radiants an Emerald side seat takes,
         owner 2026-10-06) is no centre-stone shape, so it gets no tile: one would open an empty grid. */
      var _shardMeta = window.RB_STONES_META || { shards: {} };
      var shapeList = SHAPES.filter(function (x) {
        return Object.keys(_shardMeta.shards)
          .some(function (k) { return k.split('|')[0] === x && (_shardMeta.sideOnly || []).indexOf(k) < 0; });
      });
      var allowed = setting ? setting.shapes : shapeList;
      // HOW MANY STONES ARE IN EACH SHAPE, for the origin and kind on screen.
      // Without this the shape carried over from step 1 silently hides the
      // catalogue: natural colour is 88 oval and 67 emerald but exactly ONE
      // round, so a shopper who picked a round setting sees a single stone and
      // reasonably concludes we do not stock coloured gems.
      var KINDS = (window.RB_STONES_META || {})[earringRelease ? 'earringKinds' : 'kinds'] || {};
      if (earringRelease) {
        shapeList = shapeList.filter(function (sh) { return Object.keys(KINDS).some(function (k) { return k.split('|')[0] === sh && (KINDS[k].d + KINDS[k].c) > 0; }); });
        if (!setting) allowed = shapeList;
      }
      // 🚨 ONE COUNTING FUNCTION FOR ALL THREE TOP ROWS, AND THAT IS THE POINT.
      // Stone, Formation and Shape each vary one axis of (shape, origin, kind) and
      // read the same registry, so the numbers agree with each other by
      // construction — "Diamonds 1,111" on the Stone row IS the Round tile's 1,111.
      // Counting one of them off the loaded shard instead (which would be exact for
      // the current origin, and unavailable for the other) is how diamond-browse.js
      // came to print a subset beside two totals on one screen; that mismatch was
      // visible to the owner and it is the reason this is a single helper.
      //
      // ⚠️ It is the SHARD REGISTRY, so it counts stones we stock — not stones that
      // fit the chosen setting, and not availability. Every row that shows one of
      // these numbers says so in its own sub-line.
      var kcount = function (sh, or_, kd) {
        var k = KINDS[sh + '|' + (or_ === 'lab' ? 'lab' : 'nat')];
        return k ? (kd === 'diamond' ? k.d : k.c) : 0;
      };
      // An earring tile, with no stone type chosen, counts what its page lists (see earringCount; audit M068).
      var nFor = function (x) {
        return earringRelease && !q.get('type') ? earringCount(x, origin) : kcount(x, origin, kind);
      };
      /* 🚨 IN SIDE MODE THE TAB COUNT IS THE SIDE-ELIGIBLE ONE. `kcount` reads the
         shard REGISTRY — "oval stones we stock" — which is the right number for the
         centre grid and four orders of magnitude out here: it printed "Diamonds 78"
         above a grid of 4 side-eligible stones. A count nobody qualified is a count
         read as a promise. */
      var tabCount = function (k) {
        return sideCount ? sideCount[k] : kcount(shape, origin, k);
      };
      var shapeWord = title(shape).toLowerCase();
      var kindWord = kind === 'diamond' ? 'diamonds' : 'colored stones';

      /* ---------------- the two population rows ----------------
         These govern the panel rather than sit in it: the whole diamond half of the
         rail (Carat, Color, Fancy color, Clarity, Cut, the presets) exists only on
         the diamond tab, and Formation decides which shard every bound and count on
         the page is read from. They are rendered ABOVE the rail so they survive the
         mobile drawer — see the section for why that matters.

         ⚠️ NEITHER PILL IS EVER DISABLED, however small its count. Kind and shape
         constrain each other (natural colour is 88 oval but ONE round), so greying
         "Colored stones" on a round setting would leave a shopper no way to reach
         the tab whose shape row is the only thing that could send them somewhere
         with stock. A truthful count plus the empty state is the honest pairing;
         a dead control is not. */
      // ⚠️ EACH SUB-LINE NAMES ITS OWN NUMBERS, AND THAT IS NOT DECORATION. Both
      // counts are scoped to the SHAPE on screen, so "Colored stones 1" would read
      // as "the store holds one coloured stone" — a count that misstates the
      // catalogue by four orders of magnitude. Kept to two lines in the 9.5rem
      // label column: the shape row below already says "counts are …", so these
      // drop the preamble the numbers make obvious.
      // ⚠️ A PAIRED WAVE HAS TO SAY SO, BECAUSE ITS GRID IS ROUGHLY HALF ITS COUNTS.
      // These are registry counts — stones we stock — and the earring wave then
      // filters to stock that can supply a MATCHED PAIR: measured on round natural
      // colour, 367 stocked against 174 pairable. Every other wave's gap is a few
      // percent (availability, seat fit) and reads as rounding; a 53% shortfall does
      // not, and a count nobody qualified is a count read as a promise. Naming it is
      // the honest fix — the alternative is a pairable-aware count, and the registry
      // cannot give one for the origin this page did NOT load, which would put the
      // two rows back on different bases (the whole reason `kcount` is one function).
      var pairNote = pairQty > 1 ? ' · pairs are fewer' : '';
      /* NOT RENDERED ANY MORE (owner, 2026-09-02: "Remove the notes") — the two
         `<em data-rb-sub>` slots came out of sections/ring-builder.liquid with it.
         The sentences are kept on the root as the copy for the tooltip the owner
         asked for next, so the pairs caveat above is not lost with the note. */
      root.__rbRailNotes = {
        kind: forSide ? shapeWord + ' stones that fit your side seats'
                      : shapeWord + ' stones we stock' + pairNote,
        origin: forSide ? 'side stones are priced per stone'
                        : shapeWord + ' ' + kindWord + ' we stock' + pairNote
      };
      /* 🚨 THE FORMATION LINKS DROPPED THE ENTIRE BUILD, AND HAD SINCE THEY WERE
         WRITTEN (found 2026-08-17). The section renders them with
         `?{{ request.query_string }}` — and `request.query_string` IS NOT A THING:
         Shopify's `request` exposes host, origin, path, page_type, locale and
         design_mode, nothing about the query, because full-page caching requires
         Liquid to render identically whatever the params are. So the filter
         resolved to nil on every request and the href was a bare `?`. Pressing
         Natural or Lab-Grown threw away the shopper's style, shape, seat and metal
         along with every filter, and landed them on a builder with no mounting
         chosen — a dead end reached by pressing a control that looks like a filter.
         Nothing failed, which is why it lasted: the link is valid, the page renders,
         and the loss is only visible if you knew what you had.

         THE WHOLE SEARCH STRING, NEVER A FIELD LIST. `RB.href(path, build)` would
         look like the right tool and is not: it serializes `FIELDS` off `build`,
         which drops every filter the shopper set (pmin, sp, fancy, sort…) and emits
         a stale `origin`. Every hand-listed subset of the state in this codebase has
         silently lost something.

         ⚠️ `origin` IS THE ONE PARAM THAT MUST NOT TRAVEL. It is set by the target
         page's own `data-rb-origin` section setting and re-read into `build` there
         (ring-builder-pages.js), so carrying ours would put a second, contradicting
         opinion in the URL of the page whose whole job is to change it.

         ⚠️ THE PATH IS TAKEN FROM THE ANCHOR'S OWN href, never rebuilt. The two
         handles stay written down exactly once, in the Liquid, which is what
         `verify_builder_urls.py` asserts against `RB.PAGES`. */
      var carry = new URLSearchParams(location.search);
      carry.delete('origin');
      var carryStr = carry.toString();
      ['nat', 'lab'].forEach(function (o) {
        var a = root.querySelector('[data-rb-origin-link="' + o + '"]');
        if (!a) return;
        /* An earring link also names the formation it opens (audit M068): that page then knows the shopper chose it
           and never hands them back (see the redirect in mount). Ring and pendant links are unchanged. */
        var withOrigin = earringRelease ? (carryStr ? carryStr + '&' : '') + 'origin=' + o : carryStr;
        a.setAttribute('href',
          a.getAttribute('href').split('?')[0] + (withOrigin ? '?' + withOrigin : ''));
        var n = a.querySelector('[data-rb-n]');
        /* ⚠️ BLANK IN SIDE MODE, NEVER A REGISTRY COUNT BESIDE A SIDE-ELIGIBLE ONE.
           The Stone row above now prints side-eligible figures; this row can only
           offer registry ones, because the other origin's shard is not loaded and
           fetching a second 130 KB shard to label a link is not worth it. Two counts
           on two bases in adjacent rows is exactly what `kcount` was made one
           function to prevent — an absent count is honest, a wrong one is not. */
        if (n) n.textContent = sideCount ? '' : kcount(shape, o, kind).toLocaleString();
        // An earring count, with no stone type chosen, is what the linked page lists (audit M068: "Lab-Grown 0" sat beside
        // a lab diamond, because it counted lab COLORED stones after the natural page had switched to colored). The line
        // above stays as it is: side mode keeps its blank, and verify_side_stones.py reads that line.
        if (n && !sideCount && earringRelease && !q.get('type')) n.textContent = earringCount(shape, o).toLocaleString();
      });
      if (kindsEl) {
        /* 🚨 THE STONE ROW IS A PHOTOGRAPH, NOT A PILL (owner, 2026-08-20, with
           /collections/all-gemstones?view=stone-search beside the builder). The
           reference switched on 2026-08-19 — "a diamond for Diamonds and maybe a nice
           ruby for colored stones" — and the builder was the one surface left on the
           dot. `.sb-gem` is `stone-browse.css`'s, already loaded here and already
           drawing the species row below, so the two rails line up column for column
           and there is exactly ONE tile treatment on the page.
           ⚠️ FORMATION KEEPS ITS PILL, deliberately: there is no photograph of
           "Lab-Grown" to show. That is the reference's own split, not an omission.
           ⚠️ A <button>, not an <a>: this row filters in place where the reference
           navigates. `.sb-gem` is styled by class, so the element is free.
           ⚠️ `is-current`, NOT `is-active` — this row is a SWITCHER (it loads the
           other tab's shards); the species row below is a multi-select filter. One
           row borrowing the other's state class is how the two drift apart. */
        var kindArt = {};
        [].forEach.call(
          document.querySelectorAll('[data-rb-kinds-art] [data-kind]'), function (n) {
            var u = n.getAttribute('data-img');
            if (u) kindArt[n.getAttribute('data-kind')] = u;
          });
        kindsEl.innerHTML = '';
        [['diamond', 'Diamonds'], ['color', 'Colored stones']].forEach(function (pair) {
          var k = pair[0], on = k === kind;
          var b = el('button', {
            class: 'sb-gem' + (on ? ' is-current' : ''), type: 'button',
            'aria-pressed': String(on),
            title: pair[1],
            // A press on the tab you are already on would reload the page to arrive
            // exactly where it started.
            onclick: function () { if (!on) apply({ type: k, preset: null,
              // the other population's facets stay in the URL as ACTIVE chips over a grid
              // that ignores them ("Sapphire", "Blue" above diamonds) — a population
              // switch drops them (Codex, 2026-09-02)
              sp: null, hue: null, fancy: null, offcolor: null }); }
          });
          var src = kindArt[k];
          if (src) {
            var box = el('span', { class: 'sb-gem__art' });
            var im = el('img', { src: src, alt: '', loading: 'lazy',
                                 width: '66', height: '66' });
            /* A collection image can be replaced or removed in the admin between page
               loads, and a broken <img> renders as a torn-page glyph inside the plate
               — which reads as a bug rather than as a missing photo. Falling back to
               the dot is the documented no-photograph state; the species row carries
               the identical guard. */
            im.onerror = function () {
              if (im.parentNode === box) box.removeChild(im);
              box.className = 'sb-gem__art sb-gem__art--dot sb-type__dot--' + k;
            };
            box.appendChild(im);
            b.appendChild(box);
          } else {
            // No sprite (an older template): the population gradient, which is the
            // pill's own dot. Declared once in stone-browse.css — a second inline
            // copy of that palette is the drift this repo keeps paying for.
            b.appendChild(el('span', {
              class: 'sb-gem__art sb-gem__art--dot sb-type__dot--' + k }));
          }
          b.appendChild(el('span', { class: 'sb-gem__t', text: pair[1] }));
          b.appendChild(el('span', { class: 'sb-gem__n',
                                     text: tabCount(k).toLocaleString() }));
          kindsEl.appendChild(b);
        });
      }
      // SILHOUETTES, NOT WORDS. This row used to be text-only chips while the
      // species pages drew the shape — so the same filter looked like two different
      // controls depending which page you reached it from. `RB.shapeTile` clones the
      // canonical icon, so there is one drawing of a marquise in the whole store.
      // ⚠️ The DISABLED state has to survive the port: a shape the chosen setting
      // cannot take, or one we hold no stones in, stays visible and greyed rather
      // than being dropped — seeing the whole vocabulary is what tells the shopper
      // where their setting sits in it.
      /* 🚨 NO SHAPE ROW IN SIDE MODE — IT IS A DEAD FACET AND EVERY TILE IS A DEAD
         END. The side seat fixes the shape (oval on every seat of the pilot style),
         so the row offered Round 1157, Cushion 9, Emerald 56 … and pressing any of
         them reloads a grid of 0, because `RB.fits` refuses a shape mismatch
         outright. Measured on the live page before this fix: 12 tiles, 11 of them
         one click from an empty grid. A control that cannot change the picture is
         the defect this store already has a rule about; the seat is stated in the
         banner and in the count line instead. */
      if (!forSide) rail.appendChild(facet('Shape', true, [
        RB.scroller(shapeList.map(function (x) {
          var fits = allowed.indexOf(x) >= 0;
          var n = nFor(x);
          var ok = fits && n > 0;
          var t = RB.shapeTile({
            shape: x, label: title(x), n: n ? n : '—', on: x === shape,
            onclick: function () { if (ok) apply({ shape: x }); }
          });
          if (!ok) {
            t.disabled = true;
            t.classList.add('is-off');
            /* An earring tile with no stone type chosen is off only when its shape is stocked in the other formation
               alone (the row lists no shape stocked in neither), so it says so (audit M068), in the words the line
               under this row repeats where a phone can read them (earringOnly). */
            t.title = !fits ? 'Your setting cannot take this shape'
                            : earringRelease && !q.get('type')
                              ? earringOnly(origin, 1)
                              : 'No ' + (kind === 'diamond' ? 'diamonds' : 'colored stones') +
                                ' in this shape';
          }
          return t;
        })),
        /* 🚨 A GRAY EARRING TILE SAYS WHY WHERE A PHONE CAN READ IT (owner 2026-09-25, answer 7: a shape stocked only
           in the other formation stays gray, not a link, and its reason is written visibly). The tile's tooltip is
           desktop hover only, and the string passed below as facet()'s `help` is a sr-facet-help.js KEY that names
           no entry, so it draws nothing on any device. So the reason is a line under the tiles, in the markup the setting step prints under its grayed size chips
           (rb-facet__hint rb-facet__hint--in), in the tooltip's own words (earringOnly), naming the gray tiles. It
           asks exactly what makes a tile gray with that tooltip, so the line and the tiles cannot disagree. Earring
           pages with no stone type chosen only: ring and pendant rows are unchanged (audit M068). */
        (function () {
          if (!earringRelease || q.get('type')) return null;
          var only = shapeList.filter(function (x) { return allowed.indexOf(x) >= 0 && !nFor(x); });
          return only.length ? el('p', { class: 'rb-facet__hint rb-facet__hint--in',
            text: earringOnly(origin, only.length) + ': ' + only.map(title).join(', ') }) : null;
        })()
      ], setting ? 'grayed shapes will not fit your setting'
                 : 'counts are stones we stock'));


      /* 🚨 DEFINED BEFORE ITS FIRST CALLER, AND THAT IS NOT COSMETIC. This is a
         `var` holding a function EXPRESSION: the binding hoists, the assignment
         does not — so with the definition left below the presets row, the diamond
         rail threw `priceFacet is not a function` and rendered TWO rows (Shape and
         the presets) with Fancy, Color, Clarity, Cut, Carat and Price all missing.
         Nothing looked broken from the server: the HTML was fine, the shard loaded,
         and the panel simply stopped. The same hoisting trap this repo has now paid
         for three times — caught in a BROWSER console, not by any static check. */
      /* THE TWO CONTINUOUS FORMATS, DEFINED ONCE. `raw` is what apply() writes to
         the URL, `show` what the shopper reads in the end boxes — the same {raw,
         show} pair the reference hands its contRow (diamond-browse.js), so the two
         rails print and store identically. Carat keeps two decimals; price is
         rounded to the dollar by `raw` and printed by `money`, which is where
         exactness belongs (sr-scale.js, rule 1). Price and carat are INCLUSIVE at
         both ends — passes() below tests `<` and `>` — where the graded ladders
         carry an EXCLUSIVE upper index; both are the reference's own contracts
         and neither changed here. */
      var caratFmt = { raw: function (v) { return v.toFixed(2); },
                       show: function (v) { return v.toFixed(2) + ' ct'; } };
      var priceFmt = { raw: function (v) { return Math.round(v); },
                       show: function (v) { return money(Math.round(v)); } };
      var priceFacet = function (mod) {
        return facet('Price', true, [
          RB.contRow('Price', null, st.price[0], st.price[1], pMin, pMax,
            function (a, b) { apply({ pmin: priceFmt.raw(a), pmax: priceFmt.raw(b) }); },
            priceFmt)
        ], null, mod);
      };
      /* PRICE AND THE PRESETS CLOSE THE DIAMOND RAIL — see the appends after Carat. */

      /* 🚨 PRICE IS BUILT HERE AND APPENDED BY THE BRANCHES, BECAUSE ITS POSITION IS
         PER TAB (2026-08-17). It used to be appended right here, between Shape and
         the kind-specific rows — which on the COLOURED tab dropped a half-width
         numeric row in the middle of the identity facets, so `.rb-rail`'s two-column
         grid put Price alone in one track with a hole beside it and left Carat
         orphaned four rows later. The reference pairs CARAT and PRICE at the END of
         the panel, after the rows that say what the stone IS, and that pairing is
         the whole reason the grid looks deliberate there and ragged here.
         One builder, two call sites — never two sliders to keep in step. */

      if (kind === 'diamond') {
        /* 🚨 THE DIAMOND RAIL IS THE REFERENCE'S ORDER NOW (owner, 2026-08-17: *"make
           sure diamonds look similar"*, with the two panels side by side). It ran
           Price, Carat, Color, Fancy, Clarity, Cut — the two NUMERIC rows first, so
           the tall Fancy grid landed opposite Clarity and left a screen of white
           space under the presets. /collections/all-gemstones goes
           FANCY -> COLOR|CLARITY -> CUT|CARAT -> PRICE: what the stone IS, then its
           grades, then the two numbers. Same order here, and it is the same fix the
           coloured tab got an hour earlier for the same reason. */

        // FANCY COLOUR — its own facet, and only where there is fancy stock to
        // offer. Counted off the pool rather than hard-coded, so a shape with no
        // fancy stones shows no empty row and the counts are always true.
        // Chips are HUES (five) rather than grades (twelve): "Fancy Vivid Pink"
        // and "Fancy Light Pink" are the same request, and the intensity is on the
        // card where it belongs.
        var hues = {};
        pool.forEach(function (r) {
          if (r._fancy && r._hue) hues[r._hue] = (hues[r._hue] || 0) + 1;
        });
        var hueKeys = Object.keys(hues).sort(function (a, b) { return hues[b] - hues[a]; });
        if (hueKeys.length) {
          /* 🚨 SWATCHES, NOT TEXT CHIPS. This was a `.rb-chipgrid` of word chips —
             twelve of them, four across and three deep — where the reference draws
             one scrolling row of `.sb-colour` circles. Two things were wrong at once:
             it is a different CONTROL from the one the same filter uses two clicks
             away, and being three rows tall it was the single item that unbalanced
             this panel's two tracks. A FANCY COLOUR facet with no colour on it is
             also the odd one out among three swatch rows this store already ships.
             ⚠️ The colours come from `stone-list.css`'s shared `--co-*` palette via
             `sb-colour--<hue>`; the inline `--sw` is only the floor for a hue with no
             line (Stuller's "Fa" is one). Never re-type those values here — see
             RB.swatchTile. */
          rail.appendChild(facet('Fancy color', st.fancy.length > 0, [
            RB.scroller(hueKeys.map(function (h) {
              var on = st.fancy.indexOf(h) >= 0;
              return RB.swatchTile({
                label: h, n: hues[h], on: on,
                cls: 'sb-colour--' + h.toLowerCase(),
                sw: 'var(--co-' + h.toLowerCase() +
                    ', linear-gradient(145deg,#d8cfc4,#a89e90))',
                onclick: function () {
                  var next = on ? st.fancy.filter(function (x) { return x !== h; })
                                : st.fancy.concat([h]);
                  // Picking a hue clears the colourless slider, because the two
                  // ask opposite questions and leaving a stale D–F in the URL
                  // would read as a filter that is on and doing nothing.
                  apply({ fancy: next.join(',') || null, colmin: null, colmax: null,
                          preset: null });
                }
              });
            }))
          ], 'fancy-color'));
        }

        /* OTHER COLOR — the grades that are off the K–D ladder and are not fancy.
           🚨 THE REFERENCE HAS THIS ROW AND THE BUILDER DID NOT, so those stones were
           reachable only by leaving the colour slider untouched: `passes()` has always
           dropped an off-scale stone the moment ANY colour preference is expressed
           (`st.fancy.length || !colWideOpen`), and with no chip to press there was no
           way to express "warmer than K" at all. One stone today, findable now.
           ⚠️ Bucketed through `offKey`, so a real D–Z designation reads "L-Z" and
           anything unrecognised keeps its own raw value rather than being labelled
           something false — the same function the diamond page's outlier row uses. */
        var offs = {};
        pool.forEach(function (r) {
          if (r._fancy || !isOffScale(r.color)) return;
          var k = offKey(r.color);
          offs[k] = (offs[k] || 0) + 1;
        });
        var offKeys = Object.keys(offs).sort(function (a, b) { return offs[b] - offs[a]; });
        if (offKeys.length) {
          rail.appendChild(facet('Other color', st.fancy.length > 0, [
            RB.scroller(offKeys.map(function (k) {
              var on = st.fancy.indexOf(k) >= 0;
              // Deliberately the SAME `fancy` param: `passes()` already treats an
              // off-scale grade exactly as it treats a fancy hue, so a second param
              // would be a second spelling of one rule.
              return RB.swatchTile({
                label: k, n: offs[k], on: on,
                cls: 'sb-colour--' + k.toLowerCase(),
                sw: 'var(--co-' + k.toLowerCase() +
                    ', linear-gradient(145deg,#efe7d3,#cbb98f))',
                onclick: function () {
                  var next = on ? st.fancy.filter(function (x) { return x !== k; })
                                : st.fancy.concat([k]);
                  apply({ fancy: next.join(',') || null, colmin: null, colmax: null,
                          preset: null });
                }
              });
            }))
          ], 'other-color'));
        }

        rail.appendChild(facet('Color', true, [
          RB.rangeControl('Color', COLOR, st.col[0], st.col[1],
            function (a, b) { apply({ colmin: a, colmax: b, preset: null }); }, null, true)
        ], 'color-scale'));

        rail.appendChild(facet('Clarity', true, [
          RB.rangeControl('Clarity', CLARITY, st.cla[0], st.cla[1],
            function (a, b) { apply({ clmin: a, clmax: b, preset: null }); }, null, true)
        ]));
        // ⚠️ NO HELP KEY ON CUT (2026-09-03). The owner's three panels are Fancy
        // color, Other color and Color; the note this row used to carry — "Labs
        // word the top grades differently — Excellent, Ideal and Super Ideal are
        // each their own rung here, and each report's own wording is printed on
        // the stone." — is a candidate sr-facet-help.js entry, not wired here so
        // a sentence that looks like copy is never passed as a key and ignored.
        rail.appendChild(facet('Cut', true, [
          RB.rangeControl('Cut', CUT, st.cut[0], st.cut[1],
            function (a, b) { apply({ cutmin: a, cutmax: b, preset: null }); }, null, true)
        ]));

        // CUT|CARAT then PRICE, closing the panel on its two numeric rows — the
        // reference's own tail, and the reason Carat moved off the top of this rail.
        rail.appendChild(facet('Carat', true, [
          RB.contRow('Carat', null, st.carat[0], st.carat[1], cMin, cMax,
            function (a, b) { apply({ ctmin: caratFmt.raw(a), ctmax: caratFmt.raw(b) }); },
            caratFmt)
        ]));
        /* 🚨 PRICE ALONE AND FULL WIDTH, THEN THE PRESETS AS A PILL ROW UNDER IT
           (owner, 2026-09-02: "Match that look. You can move the 'What are you
           looking for?' as a pill under the price" — with
           /collections/all-gemstones?view=stone-search beside the builder). That is
           the reference's tail: Color|Clarity, Cut|Carat, then Price on a line of its
           own. It SUPERSEDES the 2026-08-20 pairing of Price with the presets at the
           TOP of this rail — that pairing was right about the hole it closed and the
           reference has since become the standard the owner compares against.
           ⚠️ BOTH ROWS ARE `wide`, because `.rb-rail` is a two-column grid and a
           narrow row here would sit in the left track with nothing beside it — the
           very hole the 2026-08-20 pairing was made for. `priceFacet` is still the one
           builder both tabs use; the coloured rail keeps it NARROW, paired with Carat.
           ⚠️ `priceFacet` is a `var` function expression and is DEFINED above the
           branch — calling it before that definition throws and silently drops half
           the rail (the hoisting trap this file has paid for three times). */
        rail.appendChild(priceFacet('wide'));
        rail.appendChild(facet('What are you looking for?', true, [
          el('div', { class: 'rb-presets' }, [
            ['sparkle', 'Most sparkle', 'The best-cut stones — cut drives brilliance more than any other grade.'],
            ['balance', 'Best balance', 'Near-colorless and eye-clean, where most of the money goes into size.'],
            ['size', 'Biggest look', 'Maximum carat for the budget, easing color and clarity to get there.']
          ].map(function (p) {
            return el('button', {
              // the reference's pill, in its FILTER state when pressed (`is-active`, the
              // bordered plate — never `is-current`, which means "the page you are on")
              class: 'sb-type rb-preset' + (q.get('preset') === p[0] ? ' is-active' : ''),
              type: 'button', 'aria-pressed': String(q.get('preset') === p[0]),
              text: p[1],
              onclick: function () {
                // 🚨 GRADES BY NAME, NEVER BY POSITION. These were literal indices
                // (colmin: 4, clmin: 3), which silently changed meaning the moment
                // a ladder gained an entry: adding U-V and SI3/FL shifted every one
                // of them by a place, so "Best balance" quietly became H-and-better
                // instead of G. Nothing failed and nothing looked wrong — the
                // preset just stopped meaning what its own description says.
                // indexOf keeps the intent readable and immune to the next entry.
                if (q.get('preset') === p[0]) return apply({ preset: null, cutmin: null, colmin: null, clmin: null });
                if (p[0] === 'sparkle') apply({ preset: p[0], cutmin: CUT.indexOf('Ideal'), colmin: null, clmin: null });
                if (p[0] === 'balance') apply({ preset: p[0], cutmin: CUT.indexOf('Very Good'), colmin: COLOR.indexOf('G'), clmin: CLARITY.indexOf('VS2') });
                if (p[0] === 'size') apply({ preset: p[0], cutmin: CUT.indexOf('Good'), colmin: COLOR.indexOf('J'), clmin: CLARITY.indexOf('SI2') });
              }
            });
          })),
          q.get('preset') ? el('p', { class: 'rb-preset__why',
            text: ({ sparkle: 'The best-cut stones — cut drives brilliance more than any other grade.',
                     balance: 'Near-colorless and eye-clean, where most of the money goes into size.',
                     size: 'Maximum carat for the budget, easing color and clarity to get there.' })[q.get('preset')] }) : null
        ], null, 'wide'));
      } else {
        /* COLOR RAIL — species leads. Both benchmarks lead on what the stone IS.

           🚨 THIS RAIL IS THE all-gemstones PANEL'S RAIL NOW (owner, 2026-08-17:
           *"The ring builder gem search should look similar to the gemstone
           search"*). It was a 24-row `.rb-check` CHECKBOX LIST 550px tall, which
           pushed the panel's other column into a blank half-screen and looked nothing
           like the surface it is supposed to be the same control as. Three rows
           replace it —
           Stone type, Color, Carat — in the reference's own order and wearing its
           own classes, so restyling that panel restyles this one.

           ⚠️ CARAT IS SHARED CODE THAT WAS DIAMOND-ONLY BY ACCIDENT. `carats` and
           the `st.carat` bounds are computed for BOTH kinds a hundred lines up; only
           the row and the guard in `passes()` were inside the diamond branch. A
           coloured stone is sold by weight exactly like a diamond — the reference
           offers 0.19–31.77 ct on this very tab — so the honest reading is that the
           facet was missing, not that it did not apply. */
        // 🚨 `species` IS THE SHARD'S `sp`, AND UNTIL 2026-08-17 THAT WAS THE TITLE'S
        // TRAILING WORD — so this row offered "(Heated) 63", "Enhanced) 46",
        // "(Oiled) 12", "Heat) 6" and "(Untreated) 1" as stone types, five of its
        // twenty-four. The column is `custom.gem_type` now
        // (build_builder_assets.py), which is also what fixed ~85 treated diamonds
        // sitting in this tab. A fragment reappearing here means the shards are
        // stale, not that the facet is wrong.
        var speciesCount = {};
        pool.forEach(function (r) {
          if (r.species) speciesCount[r.species] = (speciesCount[r.species] || 0) + 1;
        });
        // ⚠️ ORDERED BY COUNT, WHERE THE REFERENCE ORDERS BY ITS SETTING — and the
        // reason that rule exists does not apply here. diamond-browse.js takes
        // `gem_map`'s own order because its counts are catalogue-wide and would
        // "reshuffle under the cursor every time a shopper touched another facet".
        // This row is SHAPE-SCOPED, so its counts run 110 down to 1 and the useful
        // species would otherwise be buried mid-scroller; and nothing on this panel
        // re-counts it — species are tallied off `pool`, which only shape, kind and
        // origin change, and each of those is a page load.
        var speciesKeys = Object.keys(speciesCount).sort(function (a, b) {
          return speciesCount[b] - speciesCount[a];
        });
        /* 🚨 THE SPECIES ART IS READ, NEVER RE-TYPED (owner, 2026-08-17: the row
           should "show the pictures similar to how they are on …&gem=color").
           `META.gemArt` is `sections/stone-search.liquid`'s OWN `gem_map` setting —
           26 hand-typed `Gem Type | CSS | collection-handle` lines — carried into the
           shard meta by `build_builder_assets.gem_art()`, which resolves it the way
           Shopify does (live template override if any, else the schema default the
           storefront is actually rendering) and then looks each species' collection
           photograph up against the live store.

           A section's settings are unreachable from another template in Liquid, so
           the builder cannot render that sprite; re-typing the gradients would have
           been the THIRD copy of this palette (stone-browse.liquid's `species`
           setting is the second, and its own comment records that stone-search's
           values were copied from it) — and the third copy is the one nobody updates.
           This is also why the first cut of this row shipped with no dot at all.

           🚨 IT IS A PHOTOGRAPH AS OF 2026-08-19 — `.sb-gem`, not the `.sb-type` pill
           (owner: *"Stone type filters throughout the site should not be pills, they
           should show the gems"*). The species switcher and the all-stones panel moved
           the same day and off the same stylesheet, so all three still draw one
           control. The two rows ABOVE this one stay pills on purpose: kind and
           formation are POPULATIONS, and there is no photograph of "Lab-Grown".

           ⚠️ A SPECIES WITH NO LINE STILL FILTERS. It draws the same neutral swatch
           diamond-browse.js falls back to, so a gem the owner has not given a colour
           looks unstyled rather than missing. `Diamond` has no line on purpose — this
           row only exists on the coloured tab.

           ⚠️ `gemArt` IS AN OBJECT WHERE `gemDots` WAS A STRING. A shard built before
           2026-08-19 yields undefined here and every tile falls to the neutral swatch
           — plain, but never a gradient string handed to an <img src>. */
        var ART = (window.RB_STONES_META || {}).gemArt || {};
        var NEUTRAL_DOT = 'linear-gradient(145deg,#d8cfc4,#a89e90)';
        rail.appendChild(facet('Stone type', true, [
          RB.scroller(speciesKeys.map(function (sp) {
            var on = st.species.indexOf(sp) >= 0;
            var art = ART[sp] || {};
            // ⚠️ THE COLOUR GOES IN `--gem`, NOT `background`. `.sb-gem__art--swatch`
            // paints itself with `background-image: var(--gem)`, so a `background:`
            // here is overridden by the stylesheet and every tile renders an empty
            // grey circle — the same trap `RB.swatchTile` documents for `--sw`.
            var box;
            if (art.img) {
              box = el('span', { class: 'sb-gem__art' });
              var im = el('img', { src: art.img, alt: '', loading: 'lazy',
                                   width: '66', height: '66' });
              // This URL is FROZEN into the shard (the one surface that cannot resolve
              // it live), so it can 404 between the owner replacing a collection image
              // and the next nightly rebuild. Falling back to the swatch turns that
              // into the documented no-photograph state instead of a torn-page glyph.
              im.onerror = function () {
                box.removeChild(im);
                box.className = 'sb-gem__art sb-gem__art--swatch';
                box.style.setProperty('--gem', art.dot || NEUTRAL_DOT);
              };
              box.appendChild(im);
            } else {
              box = el('span', { class: 'sb-gem__art sb-gem__art--swatch',
                                 style: '--gem:' + (art.dot || NEUTRAL_DOT) });
            }
            // `is-active`, NOT `is-current` — this is a multi-select filter, where
            // `is-current` means "the page you are on" on the species switcher.
            return el('button', {
              class: 'sb-gem' + (on ? ' is-active' : ''), type: 'button',
              'aria-pressed': String(on),
              title: sp +
                     (on ? ' — selected, press to remove' : ''),
              onclick: function () {
                var next = on ? st.species.filter(function (x) { return x !== sp; })
                              : st.species.concat([sp]);
                apply({ sp: next.join(',') || null });
              }
            }, [
              box,
              el('span', { class: 'sb-gem__t', text: sp }),
              el('span', { class: 'sb-gem__n', text: String(speciesCount[sp]) })
            ]);
          }))
        ]));

        /* COLOR — the facet a coloured-stone shopper actually arrives with, and the
           builder had none at all: the only way to ask for a blue stone was to know
           that sapphire is the species that comes in blue. `hue` is
           `custom.gem_color` ("Blue", "Padparadscha", "Color-Change"), the store's
           own filterable field.

           🚨 THE SWATCH COLOURS ARE NOT DEFINED HERE AND MUST NEVER BE. The class
           `sb-colour--<hue>` reaches `stone-list.css`'s 26 `--co-*` house colours —
           the same rule the species landing pages and the browse panel draw, which
           is why the section now loads that sheet. The inline `--sw` is a FLOOR for
           a hue the store has no line for, and it is one neutral value rather than a
           palette. Slug derivation is `h.toLowerCase()`, character for character
           what diamond-browse.js does, so a hue cannot resolve to two classes. */
        // ⚠️ `gemHues`, NOT `hues` — the diamond branch above already has a `hues`
        // for FANCY grades, and `var` is function-scoped, so two branches sharing the
        // name would hoist into one binding. Only one branch ever runs, so it is not
        // a live bug; distinct names are what keeps it from becoming one the day
        // somebody moves a block. Same reason `r.hue` and `r._hue` are worth reading
        // twice: the first is `custom.gem_color`, the second is the fancy hue parsed
        // out of a DIAMOND's colour grade. One underscore, two different fields.
        /* 🚨 COLOR COUNTS WITH STONE TYPE APPLIED — "own facet excluded, everything
           ABOVE it applied", the facet rule diamond-browse.js states and the reason
           the Stone and Formation rows above agree with each other. Counted off the
           whole pool instead, this row advertised "Blue 159" while Ruby was selected,
           and clicking it delivered ZERO: a count is a promise, and that one could
           not be kept. Stone type itself keeps counting off the full pool, because
           nothing above it in this panel is a filter — Shape and the two population
           rows ARE the pool.
           ⚠️ Carat and Price are deliberately NOT applied: they sit BELOW this row,
           and a facet that recounts against the rows under it makes every slider drag
           reshuffle the swatches above. */
        var huePool = st.species.length
          ? pool.filter(function (r) { return st.species.indexOf(r.species) >= 0; })
          : pool;
        var gemHues = {};
        huePool.forEach(function (r) { if (r.hue) gemHues[r.hue] = (gemHues[r.hue] || 0) + 1; });
        var gemHueKeys = Object.keys(gemHues).sort(function (a, b) {
          return gemHues[b] - gemHues[a];
        });
        if (gemHueKeys.length) {
          rail.appendChild(facet('Color', true, [
            RB.scroller(gemHueKeys.map(function (h) {
              var on = st.hue.indexOf(h) >= 0;
              return RB.swatchTile({
                label: h, n: gemHues[h], on: on,
                cls: 'sb-colour--' + h.toLowerCase(),
                sw: 'var(--co-' + h.toLowerCase() +
                    ', linear-gradient(145deg,#d8cfc4,#a89e90))',
                onclick: function () {
                  var next = on ? st.hue.filter(function (x) { return x !== h; })
                                : st.hue.concat([h]);
                  apply({ hue: next.join(',') || null });
                }
              });
            }))
          // `gem-color`, the gem search's key for this row. Until 2026-09-14 this slot
          // still held the pre-2026-09-03 sub-line text, an unknown key, so the
          // builder's Color row silently drew no "?" while the gem search's did.
          ], 'gem-color'));
        }

        // ─────────────────────────────────────────── THE DROPDOWN ROW ──
        // `.sb__dds` > `.sb-dd`, the gemstone search's own row and markup (owner,
        // 2026-09-06: "we should just have all the options there like in the gem
        // search ... we can match that"). Built through `RB.ddChips`, the single
        // implementation on the engine — this rail had no dropdown of its own, which
        // is why Quality first shipped as a full-width chip row that matched nothing
        // beside it.
        //
        // ⚠️ TWO OF THE SEVEN ARE DELIBERATELY ABSENT, and both would be dead here:
        //   · "Can be set" — this grid is already scoped to stones that fit the
        //     chosen wave and mounting, so every stone on screen can be set. One
        //     value, filtering nothing.
        //   · Length / Width — the SEAT fixes the millimetres. Offering them inside
        //     an already-fitting set can only exclude stones that fit, which is the
        //     opposite of what a shopper filtering by size expects.
        // Both are stated rather than quietly skipped; they are a judgement, not an
        // oversight, and the day either stops being true this is where to look.
        var dds = el('div', { class: 'sb__dds' });
        var ddCount = function (field) {
          var n = {};
          pool.forEach(function (r) { if (r[field]) n[r[field]] = (n[r[field]] || 0) + 1; });
          return n;
        };
        var ddAdd = function (o) {
          var d = RB.ddChips(o);
          if (d) dds.appendChild(d);
        };
        // ⚠️ COUNTRY OF ORIGIN is `r.country` — `r.origin` is lab vs natural, which
        // the Formation row above already owns. The engine's decoder names them
        // apart for exactly this reason.
        var orgN = ddCount('country');
        ddAdd({
          label: 'Origin', sub: 'where it was mined', help: 'origin',
          values: Object.keys(orgN).sort(function (a, b) { return orgN[b] - orgN[a]; })
                        .map(function (k) { return [k, k, orgN[k]]; }),
          applied: st.country,
          onToggle: function (next) { apply({ country: next.join(',') || null }); }
        });
        var trN = ddCount('treatment');
        ddAdd({
          label: 'Treatment', sub: 'where known', help: 'treatment',
          values: Object.keys(trN).sort(function (a, b) { return trN[b] - trN[a]; })
                        .map(function (k) { return [k, k, trN[k]]; }),
          applied: st.treatment,
          onToggle: function (next) { apply({ treatment: next.join(',') || null }); }
        });
        // ⚠️ THE SAME THREE SKIPS THE OTHER TWO SURFACES USE. "Faceted" is ~73% of
        // the catalogue and means no more than "this is a normal stone"; the other
        // two are Stuller's words for the same statement. An EXCLUSION list, never
        // an inclusion — driving it off cuts to SHOW would silently hide whatever
        // Stuller adds next.
        var csN = ddCount('cutStyle');
        ddAdd({
          label: 'Cut style',
          values: Object.keys(csN).sort(function (a, b) { return csN[b] - csN[a]; })
                        .map(function (k) { return [k, k, csN[k]]; }),
          applied: st.cutstyle, skip: ['faceted', 'faceted diamond-cut', 'precision diamond-cut'],
          onToggle: function (next) { apply({ cutstyle: next.join(',') || null }); }
        });
        // QUALITY. 🚨 THE SENTINEL LEADS — `RB.QUALITY_ORDER` is the ladder and the
        // only copy of it. Sorted by count or alphabet, "One Of A Kind" lands
        // mid-list under two-letter grades, on the one facet whose whole risk is a
        // shopper choosing AAA and never learning that every one-of-a-kind stone,
        // including all the certificated ones, was just excluded.
        var qlN = ddCount('quality');
        ddAdd({
          label: 'Quality', help: 'quality',
          sub: 'calibrated stones carry a grade; one-of-a-kind stones are described individually',
          values: Object.keys(qlN).map(function (k) { return [k, k, qlN[k]]; }),
          applied: st.quality, order: RB.QUALITY_ORDER,
          onToggle: function (next) { apply({ quality: next.join(',') || null }); }
        });
        if (dds.childNodes.length) rail.appendChild(dds);

        // CARAT THEN PRICE, the reference's own pairing — two half-width rows landing
        // side by side in `.rb-rail`'s second track after three full-width ones.
        rail.appendChild(facet('Carat', true, [
          RB.contRow('Carat', null, st.carat[0], st.carat[1], cMin, cMax,
            function (a, b) { apply({ ctmin: caratFmt.raw(a), ctmax: caratFmt.raw(b) }); },
            caratFmt)
        ]));
        rail.appendChild(priceFacet());

        // ⚠️ KEPT, AND IT IS THE ONE ROW THE REFERENCE HAS NO EQUIVALENT FOR. It
        // carries no control — it is the owner's standing promise that a softer
        // stone is listed rather than quietly withheld, and the card prints the
        // stone's own wearability flag. Last, so it never delays a filter.
        rail.appendChild(facet('Everyday wear', false, [
          el('p', { class: 'rb-facet__hint',
            // "rings" / "pendants" / "earrings" — the wave's own plural (Codex, 2026-09-02)
            text: 'Softer stones are still beautiful ' + RB.KINDS[RB.kind(RB.read())].label.replace(/^(ring|pendant)$/, '$1s') +
                  ' — they just want a gentler life. Nothing is hidden here.' })
        ]));
      }

      /* ---------------- filter + sort ---------------- */
      function passes(r) {
        if (r.price < st.price[0] || r.price > st.price[1]) return false;
        // 🚨 CARAT APPLIES TO BOTH KINDS AND USED TO BE INSIDE THE DIAMOND BRANCH.
        // A coloured stone is sold by weight exactly like a diamond, and the bounds
        // were already being computed off the whole pool — so the row simply was not
        // drawn and the guard simply was not run. Hoisted, not duplicated: one
        // expression for one rule.
        // ⚠️ `!= null`, NEVER `filter(Boolean)`-style truthiness. `0` and `null` are
        // two different statements — "measured as zero" cannot happen for a carat
        // weight, but the bounds above are built with `.filter(Boolean)`, and the day
        // those two disagree is the day a stone becomes unreachable at every slider
        // position (208 coloured stones were, on width, for exactly that reason).
        if (r.carat != null && (r.carat < st.carat[0] || r.carat > st.carat[1])) return false;
        if (kind === 'diamond') {
          // FANCY IS JUDGED BY ITS OWN FACET AND NEVER BY THE D-Z SLIDER. Before
          // this, `_col` was -1 on every fancy stone and the colour guard was
          // SKIPPED, so all 294 passed at every slider position — a Fancy Vivid
          // Blue answered a request for "D–F". Clarity and cut still apply: those
          // are graded the same way on a fancy stone.
          if (r._fancy) {
            if (st.fancy.length) {
              if (st.fancy.indexOf(r._hue) < 0) return false;
            } else if (!colWideOpen) {
              return false;
            }
          } else if (isOffScale(r.color)) {
            // OFF-SCALE (U-V and anything else not on K-D) gets the fancy treatment
            // rather than a rung of its own: shown while no colour preference is
            // expressed, dropped the moment one is. Without this branch `_col` is
            // -1, the guard below is SKIPPED and the stone matches at every position.
            if (st.fancy.length || !colWideOpen) return false;
          } else {
            // Asking for a fancy hue is asking for fancy stones only.
            if (st.fancy.length) return false;
            if (r._col >= 0 && (r._col < st.col[0] || r._col >= st.col[1])) return false;
          }
          // NOT CLARITY-GRADED IS NOT "PASSES EVERY CLARITY". A black diamond is
          // Opaque — the FL-I3 scale does not grade it — and before this the `>= 0`
          // guard was simply skipped, so it answered every clarity range there is.
          // Same treatment fancy colour gets above, and for the same reason: it is
          // not a worse SI, it is not on that axis. Still findable by its Fancy
          // colour chip, its shape, its carat and its price.
          if (r._cla >= 0) {
            if (r._cla < st.cla[0] || r._cla >= st.cla[1]) return false;
          } else if (!claWideOpen) return false;
          if (r._cut != null && (r._cut < st.cut[0] || r._cut >= st.cut[1])) return false;
        } else {
          if (st.species.length && st.species.indexOf(r.species) < 0) return false;
          // HUE. A stone with no recorded `gem_color` is dropped the moment a colour
          // IS asked for and shown while none is — the same "absence means all, but
          // absence is not a match" reading the fancy row uses. Answering "show me
          // blue" with a stone whose colour nobody recorded is answering a different
          // question.
          if (st.hue.length && st.hue.indexOf(r.hue) < 0) return false;
        }
        // ⚠️ OUTSIDE THE COLOUR BLOCK ON PURPOSE. The row only draws where it has
        // more than one value, but a `?quality=` carried in a shared URL must narrow
        // whichever tab it lands on rather than being silently ignored — the
        // "a filter that cannot change the picture" defect in reverse.
        if (st.quality.length && st.quality.indexOf(r.quality) < 0) return false;
        if (st.country.length && st.country.indexOf(r.country) < 0) return false;
        if (st.treatment.length && st.treatment.indexOf(r.treatment) < 0) return false;
        if (st.cutstyle.length && st.cutstyle.indexOf(r.cutStyle) < 0) return false;
        return true;
      }
      var rows = pool.filter(passes);

      var seatL = build.seat ? RB.parseSeat(build.seat).l : null;
      var SORTS = {
        // "Best match" is something neither benchmark can compute, because we know
        // the seat: the stone that fills the chosen center most exactly, best cut first.
        best: function (a, b) {
          if (seatL) {
            var d = Math.abs((a.l || 99) - seatL) - Math.abs((b.l || 99) - seatL);
            if (Math.abs(d) > 0.005) return d;
          }
          var c = (b._cut || 0) - (a._cut || 0);
          return c || a.price - b.price;
        },
        'price-asc': function (a, b) { return a.price - b.price; },
        'price-desc': function (a, b) { return b.price - a.price; },
        'carat-asc': function (a, b) { return (a.carat || 0) - (b.carat || 0); },
        'carat-desc': function (a, b) { return (b.carat || 0) - (a.carat || 0); }
      };
      rows.sort(SORTS[st.sort] || SORTS.best);

      /* ---- active-filter chips + reset. Both benchmarks show what is ON and
         let you remove it individually; zero vertical cost when nothing is set. */
      var activeWrap = root.querySelector('[data-rb-active]');
      if (activeWrap) {
        activeWrap.innerHTML = '';
        var chips = [];
        function chip(label, patch) { chips.push({ label: label, patch: patch }); }
        if (st.price[0] > pMin || st.price[1] < pMax)
          chip(money(st.price[0]) + ' – ' + money(st.price[1]), { pmin: null, pmax: null });
        // Carat is a chip on BOTH tabs now, because it is a filter on both — a chip
        // row that omits a live filter is the hidden state the row exists to prevent.
        if (st.carat[0] > cMin || st.carat[1] < cMax)
          chip(st.carat[0].toFixed(2) + ' – ' + st.carat[1].toFixed(2) + ' ct',
               { ctmin: null, ctmax: null });
        if (kind === 'diamond') {
          if (st.col[0] > 0 || st.col[1] < COLOR.length)
            chip('Color ' + COLOR[st.col[1] - 1] + '–' + COLOR[st.col[0]], { colmin: null, colmax: null });
          if (st.cla[0] > 0 || st.cla[1] < CLARITY.length)
            chip('Clarity ' + CLARITY[st.cla[1] - 1] + '–' + CLARITY[st.cla[0]], { clmin: null, clmax: null });
          if (st.cut[0] > 0 || st.cut[1] < CUT.length)
            chip('Cut ' + CUT[st.cut[0]] + ' and better', { cutmin: null, cutmax: null });
        }
        st.species.forEach(function (sp) {
          chip(sp, { sp: st.species.filter(function (x) { return x !== sp; }).join(',') || null });
        });
        st.hue.forEach(function (h) {
          chip(h, { hue: st.hue.filter(function (x) { return x !== h; }).join(',') || null });
        });
        st.fancy.forEach(function (h) {
          chip('Fancy ' + h,
               { fancy: st.fancy.filter(function (x) { return x !== h; }).join(',') || null });
        });
        st.quality.forEach(function (v) {
          chip(v, { quality: st.quality.filter(function (x) { return x !== v; }).join(',') || null });
        });
        st.country.forEach(function (v) {
          chip(v, { country: st.country.filter(function (x) { return x !== v; }).join(',') || null });
        });
        st.treatment.forEach(function (v) {
          chip(v, { treatment: st.treatment.filter(function (x) { return x !== v; }).join(',') || null });
        });
        st.cutstyle.forEach(function (v) {
          chip(v, { cutstyle: st.cutstyle.filter(function (x) { return x !== v; }).join(',') || null });
        });
        chips.forEach(function (c) {
          activeWrap.appendChild(el('button', {
            class: 'rb-active__chip', type: 'button',
            'aria-label': 'Remove filter ' + c.label,
            onclick: function () { apply(Object.assign({ preset: null }, c.patch)); }
          }, [el('span', { text: c.label }), el('span', { text: '✕', 'aria-hidden': 'true' })]));
        });
        if (chips.length) {
          activeWrap.appendChild(el('button', {
            class: 'rb-active__reset', type: 'button', text: 'Reset all',
            onclick: function () {
              apply({ pmin: null, pmax: null, ctmin: null, ctmax: null, colmin: null,
                      colmax: null, clmin: null, clmax: null, cutmin: null, cutmax: null,
                      sp: null, hue: null, fancy: null, preset: null });
            }
          }));
        }
        root.__activeCount = chips.length;
      }

      countEl.innerHTML = '';
      countEl.appendChild(el('span', {
        html: /* no stone count (owner 2026-10-30): the line says what the grid holds, not how many */
              (origin === 'lab' ? 'Lab-grown ' : 'Natural ') +
              (kind === 'diamond' ? (rows.length === 1 ? 'diamond' : 'diamonds')
                                  : (rows.length === 1 ? 'colored stone' : 'colored stones')) +
              /* ⚠️ THE COUNT LINE IS THE ONE PLACE THE GRID SAYS WHAT IT IS FOR, and
                 in side mode the centre's wording is wrong twice over: "that fit
                 your setting" describes the CENTRE seat, and the singular read
                 "1 natural diamond that fit your setting" — a subject/verb
                 disagreement that only ever appears at exactly one result, which is
                 the common case here because the side pool is narrow. The side
                 wording states the SEAT and the QUANTITY, because those are the two
                 facts that explain why the list is short. */
              /* ⚠️ THE VERB HAS TO AGREE, and it only ever disagrees at exactly one
                 result — "1 natural diamond that fit your setting". Rare on the
                 centre grid and COMMON here, because the side pool is narrowed to
                 one calibrated size and routinely returns a single stone. */
              (function () {
                var one = rows.length === 1, v = one ? ' that fits' : ' that fit';
                if (!setting) return '';
                if (!forSide) return v + ' your setting';
                var sd = RB.sideSeat(setting.style, build.seat);
                var sdWord = RB.sideSeatLabel ? RB.sideSeatLabel(sd.spec, null, build.style, sd.set) : RB.seatLabel(sd.spec);
                if (secondSide) return v + ' the ' + sdWord + ' second-stone seat';
                return v + ' the ' + sdWord +
                       ' side seats — available as a matched set of ' + (sd.n || 2);
              }())
      }));

      /* ---- sort: current value IN the trigger, options grouped by axis ---- */
      var SORT_LABEL = { best: 'Best match', 'price-asc': 'Price: low to high',
        'price-desc': 'Price: high to low', 'carat-asc': 'Carat: low to high',
        'carat-desc': 'Carat: high to low' };
      var sortWrap = root.querySelector('[data-rb-sort]');
      if (sortWrap) {
        sortWrap.innerHTML = '';
        var menu = el('div', { class: 'rb-sort__menu', role: 'listbox', hidden: 'hidden' });
        var btn = el('button', {
          class: 'rb-sort__btn', type: 'button', 'aria-haspopup': 'listbox',
          'aria-expanded': 'false',
          onclick: function () {
            var open = !menu.hidden;
            menu.hidden = open;
            btn.setAttribute('aria-expanded', String(!open));
          }
        }, [el('span', { text: 'Sort by: ' + (SORT_LABEL[st.sort] || 'Best match') })]);
        btn.appendChild(caret());
        [['', ['best']], ['Price', ['price-asc', 'price-desc']],
         kind === 'diamond' ? ['Carat', ['carat-asc', 'carat-desc']] : null
        ].filter(Boolean).forEach(function (grp) {
          if (grp[0]) menu.appendChild(el('div', { class: 'rb-sort__group', text: grp[0] }));
          grp[1].forEach(function (k) {
            menu.appendChild(el('button', {
              class: 'rb-sort__opt', type: 'button', role: 'option',
              'aria-selected': String(k === st.sort),
              text: grp[0] ? SORT_LABEL[k].split(': ')[1] : SORT_LABEL[k],
              onclick: function () { apply({ sort: k }); }
            }));
          });
        });
        // ⚠️ ONE CLOSER PER PAGE (see mount's note); it closes whichever menu the latest draw put in `sortWrap`.
        if (!sortWrap.__rbSortCloser) {
          sortWrap.__rbSortCloser = true;
          document.addEventListener('click', function (e) {
            if (sortWrap.contains(e.target)) return;
            var m = sortWrap.querySelector('.rb-sort__menu'), b = sortWrap.querySelector('.rb-sort__btn');
            if (m) m.hidden = true;
            if (b) b.setAttribute('aria-expanded', 'false');
          });
        }
        sortWrap.appendChild(btn);
        sortWrap.appendChild(menu);
      }

      /* ---- mobile: rail becomes a drawer ---- */
      /* 🚨 THE FILTER BAR IS MOUNTED IN TWO PLACES AND ONLY THIS ONE IS STICKY
         (2026-08-14). `ring-builder.js`'s `mountFilterDrawer` serves the SETTINGS
         step, where the bar sits inside `.rb-bar` and `.rb-bar .rb-filterbar`
         (specificity 0,2,0, no media query) pins it `position: static` — so the
         header offset is irrelevant there. The STONES step renders the same class
         OUTSIDE `.rb-bar` (see `sections/ring-builder.liquid`), so the
         `@media (width < 64rem)` rule wins and the bar really is sticky at
         `top: var(--rb-header-h, 0px)`. That makes THIS the mount that has to
         publish the offset, and it is the one the fix originally missed: the var
         went unset, `top` fell back to 0, and the bar pinned underneath the
         opaque z-50 header where the shopper could not reach Filters or Sort.
         ⚠️ The fallback cannot be raised to a constant to paper over this — a
         phone header is not the desktop's 12rem, which is exactly why
         `RB.syncHeaderOffset()` measures the element. Idempotent and shared, so
         calling it from both mounts is correct, not a duplicate. */
      var fb = root.querySelector('[data-rb-filterbar]');
      if (fb) {
        RB.watchHeaderOffset();
        /* 🚨 A REDRAW MUST NOT CLOSE, OR DOUBLE, THE DRAWER — the same rule as RB.mountFilterDrawer, which serves the
           settings grid. The scrim, Escape and the resize watcher are wired once per rail and call the NEWEST draw's
           setOpen; the open state is read off the rail and carried across the redraw. */
        var drawer = rail.__rbDrawer || (rail.__rbDrawer = {});
        var isOpen = rail.getAttribute('data-open') === 'true';
        fb.innerHTML = '';
        var n = root.__activeCount || 0;
        var openBtn = el('button', { type: 'button', 'aria-expanded': String(isOpen) },
          [el('span', { text: 'Filters' })]);
        if (n) openBtn.appendChild(el('span', { class: 'rb-fb-count', text: String(n) }));
        var scrim = root.querySelector('[data-rb-scrim]');
        /* A closed drawer is out of the tab order — see RB.syncDrawerA11y for the
           rule and why it is shared with the settings grid and re-run on resize. */
        function setOpen(open) {
          rail.setAttribute('data-open', String(open));
          RB.syncDrawerA11y(rail, open);
          if (scrim) scrim.hidden = !open;
          openBtn.setAttribute('aria-expanded', String(open));
          document.body.style.overflow = open ? 'hidden' : '';
          if (open && window.trapFocus) window.trapFocus(rail, rail.querySelector('button'));
          else if (window.removeTrapFocus) window.removeTrapFocus(openBtn);
        }
        drawer.setOpen = setOpen;
        if (!drawer.wired) {
          drawer.wired = true;
          RB.watchDrawerA11y(rail, function () { return rail.getAttribute('data-open') === 'true'; },
                             function () { drawer.setOpen(false); });
          if (scrim) scrim.addEventListener('click', function () { drawer.setOpen(false); });
          document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && rail.getAttribute('data-open') === 'true') drawer.setOpen(false);
          });
        }
        RB.syncDrawerA11y(rail, isOpen);
        openBtn.addEventListener('click', function () { setOpen(true); });
        fb.appendChild(openBtn);
        var sortBtn = el('button', { type: 'button', text: SORT_LABEL[st.sort] || 'Sort',
          onclick: function (e) {
            var b = root.querySelector('.rb-sort__btn'); if (!b) return;
            /* 🚨 THE MENU OPENED AND CLOSED IN THE SAME EVENT. The sort menu's
               outside-click closer listens on `document`, and THIS button is outside
               `sortWrap` — so the click that opened the menu (via `b.click()` below)
               bubbled on to the closer, which saw a click outside the menu and shut it.
               Measured 2026-09-02: a non-bubbling click opens it, a bubbling one does
               not. The shopper read it as "Sort does nothing" (Codex's finding; the
               off-screen half was real too, hence the scroll). Stop THIS event; the
               synthetic click on the real button bubbles from inside `sortWrap` and is
               left alone by the closer. */
            if (e && e.stopPropagation) e.stopPropagation();
            var tb = b.closest('.rb-toolbar') || b;
            tb.scrollIntoView({ block: 'center' });
            b.click();
          } });
        fb.appendChild(sortBtn);
        // drawer chrome, prepended once
        if (!rail.querySelector('.rb-rail__head')) {
          rail.insertBefore(el('div', { class: 'rb-rail__head' }, [
            el('strong', { text: 'Filters' }),
            el('button', { class: 'rb-rail__close', type: 'button', 'aria-label': 'Close filters',
              text: '✕', onclick: function () { setOpen(false); } })
          ]), rail.firstChild);
          rail.appendChild(el('div', { class: 'rb-rail__apply' }, [
            el('button', { class: 'btn btn-primary', type: 'button',
              text: 'Show results',
              onclick: function () { setOpen(false); } })
          ]));
        }
        // An open drawer's focus trap moves to the controls this draw put in it (see RB.mountFilterDrawer).
        if (isOpen && window.trapFocus) window.trapFocus(rail, rail);
      }

      /* ---------------- results ---------------- */
      grid.innerHTML = '';
      // The previous result's "View more" sits beside the grid; an empty result returns before draw() removes it.
      var staleMore = grid.parentNode && grid.parentNode.querySelector('.rb-more');
      if (staleMore) staleMore.remove();

      /* 🚨 ONLY SUGGEST THE OTHER TAB WHEN IT HAS STOCK FOR THIS SEAT — the rule the
         SIDE branch below already follows, applied to the centre grid it was never
         applied to. "switch to colored stones / diamonds" was printed unconditionally,
         so on a seat neither tab can fill it was a second dead end above the only
         live route. 🚨 MEASURED PER (STYLE, ORIGIN), NOT PER SEAT, BECAUSE THAT IS
         THE UNIT THE GRID FILTERS ON — `RB.bestSeat(setting.style, r)` asks whether a
         stone fits ANY seat of the style. A first pass measured per SEAT and reported
         7.3%; the honest figure is **111 of 400 (style, origin) combinations = 27.8%
         with BOTH tabs empty**, and the per-seat number was disproved in a browser
         (a seat it called empty rendered 4 cards). It is not a pearl problem either —
         pendant styles dominate but the ring wave hits it too. The other 61
         combinations DO have exactly one tab stocked, so the sentence must stay
         there: a blanket removal would delete a working route.
         ⚠️ IT ASKS THE KIND QUESTION ONLY. Price/clarity are the other half of the
         same sentence, so the gates applied here are the HARD ones — availability,
         seat fit (or wave eligibility when no mounting is chosen yet) and pairability
         — exactly the set the audit measures. Adding the soft filters would suppress
         the tab hint whenever the shopper's own slider emptied the grid, which is the
         one case where "widen price" is the right advice and switching tabs is not.
         ⚠️ NULL MEANS "COULD NOT TELL" AND KEEPS TODAY'S COPY. Not suggesting a tab
         that does have stock loses a real route, so an unknown degrades to the old
         behaviour rather than to silence — the shard rule this file already states. */
      var otherKindFits = (function () {
        try {
          var cand = all.filter(function (r) {
            return kind === 'diamond' ? !r._isD : r._isD;
          });
          if (setting) {
            cand = cand.filter(function (r) { return !!RB.bestSeat(setting.style, r); });
          } else {
            cand = cand.filter(function (r) {
              return RB.eligibleFor(r, forSide ? 'side' : RB.kind(build));
            });
          }
          if (pairQty > 1) cand = cand.filter(function (r) { return RB.pairable(r); });
          return cand.length;
        } catch (e) { return null; }
      })();
      var otherKindWord = kind === 'diamond' ? 'colored stones' : 'diamonds';
      // `=== 0` and never a falsy test: null is "unknown" and must keep the hint.
      var offerOtherKind = otherKindFits !== 0;

      if (!rows.length) {
        grid.appendChild(el('div', { class: 'rb-empty' }, [
          el('p', { text: 'No stones match those filters.' }),
          // A PAIRED BUILD NEEDS ITS OWN EMPTY STATE. Once the pairable gate above
          // is applied, "try widening price or clarity" can be advice for a
          // situation no filter change can fix — the shape may simply have no
          // calibrated stock. Saying so is the difference between a shopper
          // adjusting a slider forever and one switching shape.
          el('p', { text: pairQty > 1
            ? 'Earring stones come from calibrated stock only, so both stones in your '
              + 'pair are the same size. Try another shape'
              + (offerOtherKind ? ', or switch to ' + otherKindWord : '') + '.'
            : forSide
              /* ⚠️ ONLY SUGGEST THE OTHER TAB WHEN IT HAS STOCK. `sideCount` is the
                 side-eligible figure per tab and it is right here, so "try colored
                 stones" can be checked instead of guessed — on the reported seat
                 (Straight baguette 3.00 x 1.50, natural) BOTH tabs are 0 and that
                 sentence was a second dead end printed above the only live route.
                 With neither tab stocked the honest half is the centre-stone size,
                 and the formation route is appended below by the block after this. */
              ? (secondSide ? 'The second stone is cut to one calibrated size, so this seat has a short '
                            : 'Side stones are cut to one calibrated size, so this seat has a short ')
                + 'list. Try '
                + ((sideCount && (kind === 'diamond' ? sideCount.color : sideCount.diamond))
                    ? (kind === 'diamond' ? 'colored stones' : 'diamonds') + ', or '
                    : '')
                + 'a different center stone size — the ' + (secondSide ? 'second-stone' : 'side')
                + ' seat changes with it.'
              : 'Try widening price or clarity'
                + (offerOtherKind ? ' — or switch to ' + otherKindWord : '') + '.' }),
          // 🚨 REBUILD FROM `build`, NOT FROM A HAND-LISTED FIVE. This listed
          // style/shape/seat/metal/accent, so "Reset all filters" also reset the
          // shopper's CHOSEN STONE and their origin — the same partial-copy bug
          // that made the setting step drop the stone (see ring-builder-pages.js,
          // `chosen`). Resetting FILTERS must never reset the BUILD. It also wrote
          // empty strings for absent fields, which RB.read() drops but which
          // litter the address bar; RB.href omits them properly.
          /* 🚨 `shape` IS THE SIDE SHAPE IN SIDE MODE, SO WRITING IT INTO THE BUILD
             CORRUPTS THE CENTRE. This wrote `{ shape: shape }` unconditionally —
             which on a side visit stamps the SIDE shape (oval) over the shopper's
             chosen CENTRE shape and drops `for=side`, so "Reset all filters" quietly
             re-seated the ring and left side mode. Side mode keeps its own two params
             and touches nothing else. */
          el('button', { class: 'rb-active__reset', type: 'button', text: 'Reset all filters',
            onclick: function () {
              var href = forSide
                ? RB.href(location.pathname, build, { for: 'side' })
                : RB.href(location.pathname, build, { shape: shape });
              // In place like every other filter here (see apply()); a page load otherwise.
              if (root.__srClient && root.__srClient.go(href)) return;
              location.href = href;
            } })
        ]));

        /* 🚨 AN EMPTY SIDE GRID IS USUALLY A FORMATION GAP, AND THE ADVICE ABOVE
           POINTED AT TWO DEAD ENDS (owner, 2026-08-20, with a URL: 0 stones on a
           Straight baguette 3.00 x 1.50 seat). Measured across all 9 mix-and-match
           designs — 1,089 centre seats, 73 distinct side specs — NO spec is empty in
           BOTH formations, but **87 centre seats are empty in natural and stocked in
           lab** (ROUND 2.5 20 seats, STRAIGHT BAGUETTE 3.5x1.5 18, 3.0x1.5 16,
           3.75x1.75 15, ELONGATED CUSHION 5x3 7, and six smaller). On those the copy
           said "try colored stones, or a different center stone size" — and colored
           is 0 on the loaded formation too, so both roads are empty while the ONE
           road with stock goes unmentioned.
           ⚠️ THE OTHER SHARD IS FETCHED ONLY HERE, ON AN EMPTY GRID. The Formation
           row's counts stay blank by design ("an absent count is honest, a wrong one
           is not" — two counts on two bases in adjacent rows); this is a different
           question, asked once, when the page has nothing to show and the alternative
           is a dead end.
           ⚠️ THE HREF IS TAKEN FROM THE ORIGIN ANCHOR, NEVER MINTED. That anchor
           already carries the whole search string minus `origin`, which is what keeps
           `for=side` and every filter — the rule this file states twice, and the
           defect it has already been fixed for. */
        if (forSide && sideSeat) {
          (function () {
            var other = build.origin === 'lab' ? 'nat' : 'lab';
            var link = root.querySelector('[data-rb-origin-link="' + other + '"]');
            if (!link) return;
            var empty = grid.querySelector('.rb-empty');
            (RB.loadSideRows ? RB.loadSideRows(sideSeat, other, build.style)
                             : RB.loadShard(shape, other).then(function (sh) { return RB.rows(sh); }))
            .then(function (otherRows) {
              if (!empty || !empty.parentNode) return;
              var n = RB.sideCandidates(
                otherRows.filter(function (r) { return r.available; }),
                sideSeat, build.style).length;
              if (!n) return;                    // genuinely nothing anywhere — leave the copy
              var word = other === 'lab' ? 'lab-grown' : 'natural';
              empty.appendChild(el('p', { class: 'rb-empty__route' }, [
                el('span', { text: n + ' ' + word + (n === 1 ? ' stone fits' : ' stones fit')
                                   + ' this seat. ' }),
                /* The count above is UNFILTERED, so the link must land unfiltered too:
                   the origin anchor's href carries every active filter, which can
                   promise "10 fit" and land on 0 (Codex, 2026-09-02). Path from the
                   anchor (the handles stay written once, in the Liquid); params from
                   the BUILD only — `origin` dropped because the target sets its own,
                   `side` dropped because a stale one is what step 3 normalises away. */
                el('a', { href: link.getAttribute('href').split('?')[0] +
                                RB.href('', build, { 'for': 'side', origin: null, side: null }),
                          text: 'Show ' + word + (secondSide ? ' stones →' : ' side stones →') })
              ]));
            });
          })();
        }
        return;
      }

      /* ------------------------------------------------------------- compare ---
         🚨 THE BEHAVIOUR IS `stone-list.js`'s AND IS NOT REIMPLEMENTED HERE (owner,
         2026-08-17: compare "across the board"). That file owns the localStorage
         key and its VERSION, the tray, the overlay and the four-stone cap; all it
         wants from a page is one checkbox carrying a payload. A second compare
         implementation is the `chain_fit` bug waiting to happen — and here it would
         be worse than a drift, because the tray is deliberately CROSS-PAGE: a
         shopper holds a sapphire ticked on a species page and a diamond ticked here
         in the same comparison, so the two producers must agree key for key.

         ⚠️ EVERY KEY BELOW IS COPIED FROM `card-stone-row.liquid`'s SET, via
         diamond-browse.js's `listCells`. Missing keys degrade cleanly (a row the
         overlay simply does not draw); a key that means something DIFFERENT does
         not, which is why CMP_KEY was bumped v1 -> v2 the day `pn` changed units.

         🚨 `pn` IS IN CENTS. Shopify's `product.price` is an integer of cents and 21
         Liquid landing pages emit that; this shard's `p` column is DOLLARS. Mixed in
         one tray with `pn` in dollars, an $11,625 diamond scored BELOW a $545
         sapphire and was labelled "lowest" — the owner found that on 2026-08-06.

         ⚠️ `u` IS THE PRODUCT PAGE, NOT THE BUILDER STEP, AND THAT IS DELIBERATE.
         Sending it to `RB.pages.stone` with this build baked in would freeze the
         shopper's style/seat/metal into localStorage, and clicking it a week later —
         after they had changed their setting — would restore a stale build with no
         way to know it was stale. `/products/<handle>` is self-contained, is what
         every other producer stores, and `stone-set-into.liquid` is the route back
         into whichever builder they are in now. */
      function compareCell(r) {
        var cmp = {
          id: r.handle, u: '/products/' + r.handle, t: r.title,
          // M002: the compare tray's 400px square, as diamond-browse.js sends. Tested on
          // RB.sizedUrl, new with that change: a cached older engine has only thumbUrl,
          // which would store a 192px picture in the tray; without it, today's URL.
          img: (RB.sizedUrl ? RB.sizedUrl(r.img, 400, true) : r.img) || '', spin: r.video || '',
          c: RB.colourLabel(r) || '', l: r.l || '', w: r.w || '',
          sh: title(r.shape || shape), g: r.species || '', wr: r.wear || '',
          // FORMATION, not mining origin — the same fact `custom.stone_origin`
          // holds, read off which SHARD this row came out of.
          f: origin === 'lab' ? 'Lab-Grown' : 'Natural',
          ct: r.carat ? r.carat.toFixed(2) : '', cl: r.clarity || '', cg: r.cut || '',
          p: money(r.price) + (earringRelease ? ' each' : ''), pn: Math.round(r.price * 100), av: true
        };
        /* 🚨 THE SILHOUETTE IS READ OFF THE ROW, NOT CARRIED IN THE PAYLOAD, so the
           cell needs a `.sbl` ANCESTOR holding a `.sbl__ico svg`:
           `input.closest('.sbl').querySelector('.sbl__ico svg')`. Without it `p.svg`
           is '' and the overlay's TRUE-SCALE row falls back to a round outline drawn
           with `preserveAspectRatio="none"` — so every stone a shopper ticked in the
           builder became a correctly-sized ELLIPSE while looking entirely plausible.
           That is the same failure stone-list.js's `safeSvg` comment records from the
           other direction, and the first version of this cell shipped it (measured:
           `svg: ""` on every payload).

           The icon rides in a `.sbl__c--sh` cell exactly as `card-stone-row.liquid`
           puts it, which is also what hides it: `.sbl__c { display: none }` in grid
           view, and only `--cmp` opts back in. So the structure is the reference's,
           minus the six cells a builder card has no columns for.
           ⚠️ `RB.shapeArt` clones the store's canonical `gem-shape-icon` out of the
           sprite this section renders, so there is still exactly one drawing of a
           marquise — the payload carries a COPY of those paths, never a new one. */
        var ico = RB.shapeArt(r.shape || shape);
        return el('div', { class: 'sbl' }, [
          el('div', { class: 'sbl__c sbl__c--sh', 'data-l': 'Shape' },
            ico ? [el('span', { class: 'sbl__ico' }, [ico])] : null),
          el('div', { class: 'sbl__c sbl__c--cmp no-js-hidden' }, [
            el('label', { class: 'sbl-cmp' }, [
              el('input', { type: 'checkbox', class: 'sbl-cmp__i',
                            'data-cmp': JSON.stringify(cmp),
                            'aria-label': 'Compare ' + r.title }),
              el('span', { class: 'sbl-cmp__box', 'aria-hidden': 'true',
                           html: '<svg viewBox="0 0 12 12" aria-hidden="true">'
                               + '<path d="M2 6.4l2.6 2.6L10 3.4"></path></svg>' }),
              el('span', { class: 'sbl-cmp__t', text: 'Compare' })
            ])
          ])
        ]);
      }

      /* HOVER-TO-360, THE STORE'S OWN VIEWER (owner, 2026-09-02, the builder grid
         beside the diamonds collection grid). `<stone-hover-spin>` is defined by
         stone-hover-spin.js, which layout/theme.liquid loads globally with its
         stylesheet and `SR_SPIN_FIT_URL` — this page already had all three; what it
         lacked was the element. Built to the contract diamond-browse.js states:
         `.shs` wrapping `.shs__v` and `.shs__badge`, `data-src` and NO `src`, so no
         clip is fetched until the card is hovered.
         ⚠️ NO ZOOM FALLBACK HERE, DELIBERATELY. diamond-browse.js still carries the
         Liquid snippet's length estimate as a fallback; verify_spin_fit.py fails the
         build on a second copy of that arithmetic, and the element applies the
         MEASURED fit itself (`applyFit`, off `window.SRSpinFit`) as soon as the map
         loads. A stone measured after the last map build renders at zoom 1 until the
         next one, which is honest rather than wrong.
         🚨 INSIDE THE MEDIA BOX, BESIDE A PICTURE LINK — NEVER INSIDE THE TEXT LINK,
         AND NEVER WITH THE WHOLE WRAPPER AS ITS HOST. The element treats its PARENT
         as the interaction host: pointer capture on a drag and a capture-phase click
         kill after one, plus `box.querySelector('img')` for the still. Two wrong
         placements were each tried once (2026-09-02): inside `a.rb-card` puts a
         `<button>` (the badge) in an `<a>`, invalid HTML that navigates on every
         press; as a sibling of the link with `.rb-cardwrap` as host, a press-and-
         10px-slide on the TITLE or the COMPARE box cancelled the click (Codex). So
         the card is the reference's structure now — a positioned `.rb-card__media`
         holding the picture link and the viewer, and a separate text link — which
         confines the drag to the photograph and keeps the badge out of any anchor.
         🚨 CROSS-ORIGIN STILLS. These cards come from a shard, so the still is
         `cdn.shopify.com` and the canvas sample throws — the element's own CORS probe
         (`ensurePlate`) handles that. Nothing here sets `crossOrigin` on the visible
         <img>: a refused CORS fetch renders NOTHING, not a fallback. */
      function spinViewer(r) {
        if (!r.video) return null;                 // no clip filmed — still only, no badge
        var host = el('stone-hover-spin', { class: 'shs', 'data-src': r.video });
        var v = el('video', { class: 'shs__v', muted: 'muted', loop: 'loop',
          playsinline: 'playsinline', preload: 'none',
          disablepictureinpicture: 'disablepictureinpicture',
          tabindex: '-1', 'aria-hidden': 'true' });
        v.muted = true;                            // the attribute alone does not autoplay
        var badge = el('button', { type: 'button', class: 'shs__badge',
          'aria-label': r.title + ' — play 360 degree rotating view' });
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '1.8');
        svg.setAttribute('aria-hidden', 'true');
        svg.innerHTML = '<path d="M3 12a9 9 0 1 0 3-6.7"></path><path d="M3 4v5h5"></path>';
        badge.appendChild(svg);
        badge.appendChild(el('span', { text: '360°' }));
        host.appendChild(v);
        host.appendChild(badge);
        return host;
      }

      var PAGE = 60, shown = 0;
      function draw() {
        rows.slice(shown, shown + PAGE).forEach(function (r) {
          // A PEARL is graded on none of carat / colour / clarity / cut — it is sold
          // by millimetre, body colour and a letter quality (AA, AAA). Running it
          // through either branch below printed a bare "7.00 mm · Pearl" and dropped
          // everything a pearl buyer actually chooses on. Owner decision 2026-07-29:
          // size · type · quality. Type and quality are read off the title, which is
          // where they already live ("7 mm White Cultured Akoya Pearl – Round Shape
          // – Half-Drilled – AA Quality").
          var spec;
          if (r._isPearl) {
            var t = r.title || '';   // RB.rows exposes `title`; `t` is the shard COLUMN name
            var mm = r.l ? (r.l % 1 ? r.l.toFixed(1) : r.l.toFixed(0)) + ' mm' : null;
            var body = (t.match(/\b(White|Black|Pink|Peach|Lavender|Golden|Grey|Gray)\b/) || [])[1];
            var kind = (t.match(/\b(Akoya|Freshwater|South Sea|Tahitian)\b/) || [])[1];
            var qual = (t.match(/–\s*(AAA|AA\+?|A)\s*Quality/i) || [])[1];
            spec = [mm, [body, kind].filter(Boolean).join(' ') || 'Cultured Pearl', qual]
              .filter(Boolean).join(' · ');
          } else if (r._isD) {
            spec = [r.carat ? r.carat.toFixed(2) + ' ct' : null, r.color, r.clarity, r.cut]
              .filter(Boolean).join(' · ');
          } else {
            spec = [r.carat ? r.carat.toFixed(2) + ' ct' : (r.l ? r.l.toFixed(2) + ' mm' : null), r.species]
              .filter(Boolean).join(' · ');
          }
          /* 🚨 THE COMPARE BOX CANNOT LIVE INSIDE THE CARD, BECAUSE THE CARD IS AN
             `<a>`. A checkbox nested in a link is invalid HTML and, worse, it works
             badly rather than not at all: the tick registers AND the browser follows
             the href, so the shopper is navigated off the grid by the control whose
             whole purpose is to keep them on it. The reference has no such problem —
             `.card-product` is a div whose title and image are the links — so the
             builder grows the wrapper the reference already had.
             ⚠️ `.rb-cardwrap` is the GRID ITEM now, not `.rb-card`. Anything styling
             `.rb-grid > *` has to be checked against that (ring-builder.css). */
          /* 🚨 A SIDE PICK GOES STRAIGHT BACK TO REVIEW, AND IT MUST NOT TOUCH THE
             SEAT. The centre's card re-snaps `seat` to whichever seat holds that
             stone — right for a centre, catastrophic for a side: the centre stone
             and the whole side lookup are keyed on `build.seat`, so re-snapping it
             here would silently re-seat the ring around the side stone and orphan
             the centre the shopper already chose. It also skips the stone DETAIL
             page, which is written about a centre stone (it offers "set this into a
             ring") and would read as a second centre-stone choice. */
          /* ⚠️ THE CHOSEN SIDE STONE IS MARKED, because in side mode one of these
             cards is already in the build — the default — and an unmarked grid tells
             the shopper nothing has been decided when in fact it has. */
          var _chosen = forSide && build.side && String(r.vid) === String(build.side);
          /* TWO LINKS TO ONE PLACE, AS ON EVERY COLLECTION CARD IN THE STORE: the
             picture link is the keyboard's stop (it is the box the focus ring draws
             around, and it survives the nine-up view where the text is hidden), the
             text link is `tabindex="-1"` so a card is one Tab, not two. See spinViewer
             above for why the media box is outside the text link.
             ⚠️ THE `href:` KEY STAYS ON A LITERAL TERNARY. verify_side_stones.py [5]
             extracts the SIDE branch by `href: forSide ? RB.href… : RB.href…` to prove
             a side pick carries no seat; hoisting it into a `var` made both the check
             and its control fail together — the stale-anchor signature. */
          var pic = el('a', {
            class: 'rb-card__pic', 'aria-label': r.title,
            // carry the seat that actually holds THIS stone, not the one chosen
            // back in step 1b before the customer knew what they wanted
            href: forSide
              ? RB.href(RB.pages.review, build, { side: r.vid })
              : RB.href(RB.pages.stone, build, {
                  stone: r.vid, origin: origin, shape: shape,
                  seat: (setting && RB.bestSeat(setting.style, r)) || build.seat || null
                })
          }, [
            /* M002: THE REFERENCE GRID'S OWN FILE (owner 2026-09-02: this grid IS the
               reference's grid). diamond-browse.js imgEl draws sized(r.img, 600) —
               600px, square, crop=center, one file, no srcset — and so does this,
               through RB.sizedUrl; a cached older engine without it draws today's
               800px original. stone-hover-spin reads this img's currentSrc for its
               40px plate probe, which drops crop and height itself. */
            r.img ? el('img', { src: RB.sizedUrl ? RB.sizedUrl(r.img, 600, true) : r.img, alt: '', loading: 'lazy' })
                  : el('span', { class: 'rb-noimg', text: 'Photograph to follow' })
          ]);
          var card = el('a', {
            class: 'rb-card' + (_chosen ? ' is-chosen' : ''),
            href: pic.getAttribute('href'), tabindex: '-1'
          }, [
            el('span', { class: 'rb-card__body' }, [
              el('span', { class: 'rb-card__title', text: r.title }),
              el('span', { class: 'rb-card__spec', text: spec }),
              el('span', { class: 'rb-card__price', text: money(r.price) + (earringRelease ? ' each' : '') })
            ])
          ]);
          var media = el('div', { class: 'rb-card__media' }, [
            pic,
            r.wear && r.wear.indexOf('Excellent') < 0
              ? el('span', { class: 'rb-card__flag', text: r.wear }) : null,
            spinViewer(r)
          ]);
          grid.appendChild(el('div', { class: 'rb-cardwrap' + (_chosen ? ' is-chosen' : '') },
                              [media, card, compareCell(r)]));
        });
        shown += PAGE;
        // 🚨 RE-TICK WHAT STORAGE ALREADY HOLDS. `stone-list.js` watches
        // `[data-sbl-grid]`/`#product-grid` with a MutationObserver to do this for a
        // paginated Liquid grid; this grid is neither and must not claim that
        // attribute (it also selects the list-view rules, which would relay out the
        // builder's grid for any shopper who once chose list view elsewhere). So the
        // shared `sync` is called directly — the same function, never a second reader
        // of the compare key. Absent (script blocked, load order), cards render
        // unticked, which is exactly today's behaviour.
        if (window.SRCompare && window.SRCompare.sync) window.SRCompare.sync();
        var old = grid.parentNode.querySelector('.rb-more');
        if (old) old.remove();
        if (shown < rows.length) {
          grid.parentNode.appendChild(el('button', {
            class: 'btn btn-outline rb-more', type: 'button',
            text: 'View more',
            onclick: draw
          }));
        }
      }
      draw();
    });
  }

  RB.mountStones = mount;
  // ⚠️ This copy draws again in place (the `token`, see mount). ring-builder-pages.js checks for it: a cached previous
  // copy cannot, so its picker keeps its page loads until the caches catch up.
  mount.redraws = true;

  // 🚨 THE GRADE SCALES ARE EXPORTED, AND THIS FILE IS THEIR ONLY DEFINITION.
  // `diamond-browse.js` (the collection-page browser) filters on exactly the same
  // ladders, and a second copy of them is the `chain_fit` bug waiting to happen —
  // two implementations of one rule that agree until the day someone edits one.
  // `verify_diamond_assets.py` parses THIS file and fails on any grade the shards
  // carry that the ladder does not, so keeping one definition here keeps that
  // check meaningful for both surfaces.
  RB.SCALES = {
    COLOR: COLOR, CLARITY: CLARITY, CUT: CUT, CUT_MAP: CUT_MAP,
    isFancy: isFancy, fancyHue: fancyHue, isOffScale: isOffScale, offKey: offKey,
    colIndex: colIndex, claIndex: claIndex
  };
})();
